import { Fragment, type CSSProperties, type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from '@/app/session';
import { ApiError, api, download, getBlob } from '@/lib/api/client';
import { Badge, Button, EmptyState, ErrorState, LoadingState, Panel } from '@/ui/primitives';

type Section = 'overview' | 'queues' | 'dispositions' | 'leads' | 'rules' | 'providers' | 'connect' | 'integrations' | 'branding' | 'ivr' | 'licence';

interface Catalog {
  counts: Record<string, number>;
  deletion_policy: Record<string, string>;
  can_purge: boolean;
}

interface Disposition {
  id: string;
  code: string;
  name: string;
  outcome: string;
  is_active: boolean;
  requires_notes: boolean;
  requires_callback: boolean;
  adds_to_dnc: boolean;
  retry_after_minutes: number | null;
}

interface Lead {
  id: string;
  name: string;
  phone_e164: string;
  email: string | null;
  status: string;
  priority: number;
  source: string;
  list_name: string;
  is_active: boolean;
  owner_id: string | null;
}

interface Rule {
  id: string;
  campaign_id: string;
  rule_type: string;
  match_key: string;
  action: string;
  retry_after_minutes: number | null;
  max_attempts: number | null;
  is_active: boolean;
}

interface Integration {
  id: string;
  provider: string;
  name: string;
  status: string;
  secret_ref: string;
  config: Record<string, unknown>;
  is_enabled: boolean;
}

interface Campaign {
  id: string;
  name: string;
}

interface Branding {
  product_name: string;
  company_name: string;
  browser_title: string;
  logo_url: string;
  primary_color: string;
  accent_color: string;
  support_email: string;
  legal_notice: string;
}

interface OverviewSummary {
  metrics: Record<string, number | null>;
}

interface CapacityInfo {
  plan: string;
  status: string;
  licensed_seats: number;
  active_users: number;
  available_seats: number;
  concurrent_call_limit: number;
  utilization_pct: number;
  entitlements: Record<string, unknown>;
  period_start: string | null;
  period_end: string | null;
}

interface TrendRow {
  date: string;
  calls: number;
  handled: number;
  abandoned: number;
  talk_seconds: number;
  avg_talk_seconds: number | null;
  avg_hold_seconds: number | null;
  aht_seconds: number | null;
}

interface ActivityRow {
  id: string;
  kind: 'lead' | 'call';
  occurred_at: string;
  direction: 'inbound' | 'outbound' | null;
  name: string;
  phone: string;
  agent_id: string | null;
  agent_name: string;
  status: string;
  answered: boolean | null;
  duration_seconds: number | null;
  source?: string;
}

interface ActivityResponse {
  rows: ActivityRow[];
}

interface TrendResponse {
  rows: TrendRow[];
}

interface OverviewUser {
  id: string;
  full_name: string;
  agent_state: string | null;
}

const inputClass = 'h-8 rounded-md border border-line bg-bg px-2 text-base';
const sections: { id: Section; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'queues', label: 'Queues & routing' },
  { id: 'ivr', label: 'IVR menus' },
  { id: 'dispositions', label: 'Dispositions' },
  { id: 'leads', label: 'Leads & data' },
  { id: 'rules', label: 'Dial rules' },
  { id: 'providers', label: 'Calling provider' },
  { id: 'connect', label: 'Connect setup' },
  { id: 'licence', label: 'Licence & seats' },
  { id: 'integrations', label: 'Accounts & integrations' },
  { id: 'branding', label: 'Branding' },
];

function message(error: unknown): string {
  return error instanceof ApiError ? error.message : 'The action could not be completed';
}

function parseJson(value: string, label: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value || '{}') as unknown;
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') throw new Error();
    return parsed as Record<string, unknown>;
  } catch {
    throw new Error(`${label} must be a valid JSON object`);
  }
}

export function AdminControlCenter() {
  const { can } = useSession();
  const [section, setSection] = useState<Section>('overview');
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [dispositions, setDispositions] = useState<Disposition[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [agents, setAgents] = useState<OverviewUser[]>([]);
  const [branding, setBranding] = useState<Branding>({
    product_name: 'Konnextions',
    company_name: 'PsYc+God AI&ML',
    browser_title: 'Konnextions by PsYc+God AI&ML',
    logo_url: '',
    primary_color: '#6D5EF5',
    accent_color: '#14B8A6',
    support_email: '',
    legal_notice: '',
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMapping, setImportMapping] = useState<Record<string, string> | null>(null);

  const [dispositionForm, setDispositionForm] = useState({
    code: '', name: '', outcome: 'connected', retry: '', requires_notes: false,
    requires_callback: false, adds_to_dnc: false,
  });
  const [leadForm, setLeadForm] = useState({
    first_name: '', last_name: '', phone_e164: '', email: '', source: 'manual', list_name: '',
  });
  const [ruleForm, setRuleForm] = useState({
    campaign_id: '', rule_type: 'retry', match_key: '', action: 'retry', retry: '', max: '',
  });
  const [integrationForm, setIntegrationForm] = useState({
    provider: '', name: '', secret_ref: '', config: '{}', enabled: false,
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [nextCatalog, nextDispositions, nextLeads, nextRules, nextIntegrations, nextCampaigns, nextAgents] =
        await Promise.all([
          api.get<Catalog>('/admin/config/catalog'),
          api.get<Disposition[]>('/admin/config/dispositions').catch(() => []),
          api.get<Lead[]>(`/admin/config/leads?include_archived=true&search=${encodeURIComponent(search)}`).catch(() => []),
          api.get<Rule[]>('/admin/config/rules').catch(() => []),
          api.get<Integration[]>('/admin/config/integrations').catch(() => []),
          api.get<Campaign[]>('/campaigns').catch(() => []),
          api.get<OverviewUser[]>('/admin/users').catch(() => []),
        ]);
      setCatalog(nextCatalog);
      setDispositions(nextDispositions);
      setLeads(nextLeads);
      setRules(nextRules);
      setIntegrations(nextIntegrations);
      setCampaigns(nextCampaigns);
      setAgents(nextAgents);
      const currentBranding = await api.get<Partial<Branding>>('/meta/branding').catch(() => null);
      if (currentBranding) setBranding((value) => ({ ...value, ...currentBranding }));
      setError(null);
    } catch (caught) {
      setError(message(caught));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { void refresh(); }, [refresh]);

  const act = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setNotice(success);
      await refresh();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const purge = (path: string, label: string) => {
    if (!catalog?.can_purge) return;
    if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return;
    void act(() => api.del(path), `${label} permanently deleted`);
  };

  const createDisposition = (event: FormEvent) => {
    event.preventDefault();
    void act(
      () => api.post('/admin/config/dispositions', {
        code: dispositionForm.code.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
        name: dispositionForm.name,
        outcome: dispositionForm.outcome,
        retry_after_minutes: dispositionForm.retry ? Number(dispositionForm.retry) : null,
        requires_notes: dispositionForm.requires_notes,
        requires_callback: dispositionForm.requires_callback,
        adds_to_dnc: dispositionForm.adds_to_dnc,
      }),
      'Disposition added',
    );
  };

  const createLead = (event: FormEvent) => {
    event.preventDefault();
    void act(() => api.post('/admin/config/leads', {
      ...leadForm,
      email: leadForm.email || null,
    }), 'Lead added');
  };

  const createRule = (event: FormEvent) => {
    event.preventDefault();
    void act(() => api.post('/admin/config/rules', {
      campaign_id: ruleForm.campaign_id,
      rule_type: ruleForm.rule_type,
      match_key: ruleForm.match_key,
      action: ruleForm.action,
      retry_after_minutes: ruleForm.retry ? Number(ruleForm.retry) : null,
      max_attempts: ruleForm.max ? Number(ruleForm.max) : null,
    }), 'Dial rule added');
  };

  const createIntegration = (event: FormEvent) => {
    event.preventDefault();
    let config: Record<string, unknown>;
    try { config = parseJson(integrationForm.config, 'Configuration'); }
    catch (caught) { setError(message(caught)); return; }
    void act(() => api.post('/admin/config/integrations', {
      provider: integrationForm.provider.trim().toLowerCase(),
      name: integrationForm.name,
      secret_ref: integrationForm.secret_ref,
      config,
      is_enabled: integrationForm.enabled,
    }), 'Integration added');
  };

  const previewImport = async () => {
    if (!importFile) return;
    const data = new FormData();
    data.append('file', importFile);
    data.append('duplicate_strategy', 'skip');
    setBusy(true);
    try {
      const preview = await api.postForm<{ mapping: Record<string, string>; created: number; rejected: number }>(
        '/imports/preview', data,
      );
      setImportMapping(preview.mapping);
      setNotice(`Preview ready: ${preview.created ?? 0} new, ${preview.rejected ?? 0} rejected`);
    } catch (caught) { setError(message(caught)); }
    finally { setBusy(false); }
  };

  const executeImport = () => {
    if (!importFile || !importMapping) return;
    const data = new FormData();
    data.append('file', importFile);
    data.append('mapping_json', JSON.stringify(importMapping));
    data.append('duplicate_strategy', 'skip');
    void act(() => api.postForm('/imports', data), 'Lead import completed');
  };

  if (loading && !catalog) return <LoadingState label="Loading control center" />;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-bg">
      <header className="flex shrink-0 items-center gap-2 border-b border-line bg-surface px-3 py-2">
        <div>
          <h1 className="text-md font-semibold">Administration control center</h1>
          <p className="text-xs text-faint">Dynamic tenant, dialer, CRM and provider configuration</p>
        </div>
        <Badge tone={catalog?.can_purge ? 'danger' : 'brand'} className="ml-auto">
          {catalog?.can_purge ? 'Super Admin · server deletion enabled' : 'Administrator · protected deletion'}
        </Badge>
      </header>

      <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-line bg-surface px-2 py-1" aria-label="Administration sections">
        {sections.map((item) => (
          <Button key={item.id} size="sm" variant={section === item.id ? 'solid' : 'ghost'} tone="brand" onClick={() => setSection(item.id)}>
            {item.label}
          </Button>
        ))}
      </nav>

      {error ? <div className="border-b border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">{error}</div> : null}
      {notice ? <div className="flex border-b border-ok/30 bg-ok-soft px-3 py-2 text-xs text-ok">{notice}<button className="ml-auto" onClick={() => setNotice(null)}>Dismiss</button></div> : null}

      <main className="min-h-0 flex-1 overflow-auto p-3">
        {section === 'overview' ? (
          <OverviewDashboard catalog={catalog} />
        ) : null}

        {section === 'dispositions' ? (
          <div className="grid gap-3 lg:grid-cols-[360px_1fr]">
            <Panel title="Add disposition" bodyClassName="p-3">
              <form className="grid gap-2" onSubmit={createDisposition}>
                <input required className={inputClass} placeholder="Code (e.g. CALLBACK)" value={dispositionForm.code} onChange={(e) => setDispositionForm({ ...dispositionForm, code: e.target.value })} />
                <input required className={inputClass} placeholder="Display name" value={dispositionForm.name} onChange={(e) => setDispositionForm({ ...dispositionForm, name: e.target.value })} />
                <select className={inputClass} value={dispositionForm.outcome} onChange={(e) => setDispositionForm({ ...dispositionForm, outcome: e.target.value })}>
                  {['connected', 'not_connected', 'callback', 'sale', 'resolved', 'not_interested', 'do_not_call', 'wrong_number', 'voicemail', 'abandoned'].map((value) => <option key={value}>{value}</option>)}
                </select>
                <input className={inputClass} type="number" min="0" placeholder="Retry after minutes" value={dispositionForm.retry} onChange={(e) => setDispositionForm({ ...dispositionForm, retry: e.target.value })} />
                {([['requires_notes', 'Require notes'], ['requires_callback', 'Require callback time'], ['adds_to_dnc', 'Add contact to DNC']] as const).map(([key, label]) => (
                  <label key={key} className="flex gap-2 text-xs"><input type="checkbox" checked={dispositionForm[key]} onChange={(e) => setDispositionForm({ ...dispositionForm, [key]: e.target.checked })} />{label}</label>
                ))}
                <Button type="submit" variant="solid" tone="brand" disabled={busy}>Add disposition</Button>
              </form>
            </Panel>
            <Panel title="Available dispositions">
              {dispositions.length ? <table className="w-full text-base"><thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-2">Code / name</th><th>Outcome</th><th>Rules</th><th>State</th><th>Actions</th></tr></thead><tbody>
                {dispositions.map((item) => <tr key={item.id} className="border-b border-line/60"><td className="p-2"><code>{item.code}</code><div>{item.name}</div></td><td>{item.outcome}</td><td className="text-xs text-muted">{[item.requires_notes && 'notes', item.requires_callback && 'callback', item.adds_to_dnc && 'DNC', item.retry_after_minutes != null && `retry ${item.retry_after_minutes}m`].filter(Boolean).join(' · ') || 'None'}</td><td><Badge tone={item.is_active ? 'ok' : 'neutral'}>{item.is_active ? 'active' : 'inactive'}</Badge></td><td><div className="flex gap-1"><Button size="sm" onClick={() => { const name = window.prompt('Disposition name', item.name); if (name) void act(() => api.patch(`/admin/config/dispositions/${item.id}`, { name }), 'Disposition updated'); }}>Edit</Button><Button size="sm" disabled={!item.is_active} onClick={() => void act(() => api.del(`/admin/config/dispositions/${item.id}`), 'Disposition deactivated')}>Deactivate</Button>{catalog?.can_purge ? <Button size="sm" tone="danger" onClick={() => purge(`/admin/config/dispositions/${item.id}/purge`, item.name)}>Purge</Button> : null}</div></td></tr>)}
              </tbody></table> : <EmptyState message="No dispositions" hint="Add the first dynamic disposition." />}
            </Panel>
          </div>
        ) : null}

        {section === 'leads' ? (
          <div className="space-y-3">
            <div className="grid gap-3 lg:grid-cols-2">
              <Panel title="Add lead" bodyClassName="p-3"><form className="grid grid-cols-2 gap-2" onSubmit={createLead}>
                <input className={inputClass} placeholder="First name" value={leadForm.first_name} onChange={(e) => setLeadForm({ ...leadForm, first_name: e.target.value })} />
                <input className={inputClass} placeholder="Last name" value={leadForm.last_name} onChange={(e) => setLeadForm({ ...leadForm, last_name: e.target.value })} />
                <input required className={inputClass} placeholder="+919876543210" value={leadForm.phone_e164} onChange={(e) => setLeadForm({ ...leadForm, phone_e164: e.target.value })} />
                <input className={inputClass} type="email" placeholder="Email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} />
                <input className={inputClass} placeholder="Source" value={leadForm.source} onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })} />
                <input className={inputClass} placeholder="List name" value={leadForm.list_name} onChange={(e) => setLeadForm({ ...leadForm, list_name: e.target.value })} />
                <Button className="col-span-2" type="submit" variant="solid" tone="brand" disabled={busy}>Add lead</Button>
              </form></Panel>
              <Panel title="Import / export" bodyClassName="space-y-2 p-3">
                <input type="file" accept=".csv,.tsv,.xlsx" onChange={(e) => { setImportFile(e.target.files?.[0] ?? null); setImportMapping(null); }} />
                <div className="flex gap-2"><Button disabled={!importFile || busy} onClick={() => void previewImport()}>1. Preview & map</Button><Button variant="solid" tone="brand" disabled={!importMapping || busy} onClick={executeImport}>2. Confirm import</Button><Button disabled={!can('lead:export')} onClick={() => void download('/admin/config/leads-export', 'konnextions-leads.csv')}>Export CSV</Button></div>
                <p className="text-xs text-faint">Imports are dry-run previewed first. Duplicate rows are skipped by default and every job is retained in import history.</p>
              </Panel>
            </div>
            <Panel title="Lead database" actions={<div className="flex gap-1"><input className={inputClass} placeholder="Search name or phone" value={search} onChange={(e) => setSearch(e.target.value)} /><Button size="sm" onClick={() => void refresh()}>Search</Button></div>}>
              {leads.length ? <table className="w-full text-base"><thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-2">Contact</th><th>Status</th><th>Assigned agent</th><th>Source / list</th><th>Actions</th></tr></thead><tbody>{leads.map((lead) => <tr key={lead.id} className="border-b border-line/60"><td className="p-2"><div>{lead.name || 'Unnamed lead'}</div><div className="knx-num text-xs text-muted">{lead.phone_e164} {lead.email ? `· ${lead.email}` : ''}</div></td><td><Badge tone={lead.is_active ? 'info' : 'neutral'}>{lead.is_active ? lead.status : 'archived'}</Badge></td><td className="p-1"><select aria-label={`Assign ${lead.name || lead.phone_e164}`} className={inputClass} value={lead.owner_id ?? ''} disabled={!lead.is_active || !can('lead:assign')} onChange={(event) => { const ownerId = event.target.value; if (ownerId) void act(() => api.patch(`/admin/config/leads/${lead.id}`, { owner_id: ownerId }), 'Lead assigned'); }}><option value="">Unassigned</option>{agents.filter((agent) => agent.agent_state !== null).map((agent) => <option key={agent.id} value={agent.id}>{agent.full_name}</option>)}</select></td><td>{lead.source}<div className="text-xs text-faint">{lead.list_name || 'No list'}</div></td><td><div className="flex gap-1"><Button size="sm" disabled={!lead.is_active} onClick={() => { const status = window.prompt('Status', lead.status); if (status) void act(() => api.patch(`/admin/config/leads/${lead.id}`, { status }), 'Lead updated'); }}>Edit</Button><Button size="sm" disabled={!lead.is_active} onClick={() => void act(() => api.del(`/admin/config/leads/${lead.id}`), 'Lead archived')}>Archive</Button>{catalog?.can_purge ? <Button size="sm" tone="danger" onClick={() => purge(`/admin/config/leads/${lead.id}/purge`, lead.name || lead.phone_e164)}>Purge</Button> : null}</div></td></tr>)}</tbody></table> : <EmptyState message="No leads found" />}
            </Panel>
          </div>
        ) : null}

        {section === 'rules' ? (
          <div className="grid gap-3 lg:grid-cols-[360px_1fr]">
            <Panel title="Add campaign rule" bodyClassName="p-3"><form className="grid gap-2" onSubmit={createRule}>
              <select required className={inputClass} value={ruleForm.campaign_id} onChange={(e) => setRuleForm({ ...ruleForm, campaign_id: e.target.value })}><option value="">Select campaign</option>{campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}</select>
              <input required className={inputClass} placeholder="Rule type" value={ruleForm.rule_type} onChange={(e) => setRuleForm({ ...ruleForm, rule_type: e.target.value })} />
              <input className={inputClass} placeholder="Match key / disposition" value={ruleForm.match_key} onChange={(e) => setRuleForm({ ...ruleForm, match_key: e.target.value })} />
              <input required className={inputClass} placeholder="Action" value={ruleForm.action} onChange={(e) => setRuleForm({ ...ruleForm, action: e.target.value })} />
              <div className="grid grid-cols-2 gap-2"><input className={inputClass} type="number" min="0" placeholder="Retry minutes" value={ruleForm.retry} onChange={(e) => setRuleForm({ ...ruleForm, retry: e.target.value })} /><input className={inputClass} type="number" min="0" placeholder="Max attempts" value={ruleForm.max} onChange={(e) => setRuleForm({ ...ruleForm, max: e.target.value })} /></div>
              <Button type="submit" variant="solid" tone="brand" disabled={!ruleForm.campaign_id || busy}>Add rule</Button>
            </form></Panel>
            <Panel title="Dial rules">{rules.length ? <table className="w-full text-base"><thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-2">Type / match</th><th>Action</th><th>Limits</th><th>State</th><th>Actions</th></tr></thead><tbody>{rules.map((rule) => <tr key={rule.id} className="border-b border-line/60"><td className="p-2">{rule.rule_type}<div className="text-xs text-faint">{rule.match_key || 'Any'}</div></td><td>{rule.action}</td><td>{rule.retry_after_minutes ?? '—'} min / {rule.max_attempts ?? '—'} attempts</td><td><Badge tone={rule.is_active ? 'ok' : 'neutral'}>{rule.is_active ? 'active' : 'inactive'}</Badge></td><td><div className="flex gap-1"><Button size="sm" onClick={() => void act(() => api.patch(`/admin/config/rules/${rule.id}`, { is_active: !rule.is_active }), 'Rule updated')}>{rule.is_active ? 'Deactivate' : 'Activate'}</Button>{catalog?.can_purge ? <Button size="sm" tone="danger" onClick={() => purge(`/admin/config/rules/${rule.id}/purge`, rule.rule_type)}>Purge</Button> : null}</div></td></tr>)}</tbody></table> : <EmptyState message="No campaign rules" />}</Panel>
          </div>
        ) : null}

        {section === 'providers' ? (
          <ProvidersManager busy={busy} act={act} />
        ) : null}

        {section === 'connect' ? (
          <ConnectSetup />
        ) : null}

        {section === 'licence' ? (
          <LicenceManager busy={busy} act={act} />
        ) : null}

        {section === 'integrations' ? (
          <div className="grid gap-3 lg:grid-cols-[400px_1fr]">
            <Panel title="Connect provider account" bodyClassName="p-3"><form className="grid gap-2" onSubmit={createIntegration}>
              <input required className={inputClass} placeholder="Provider key (twilio, hubspot, custom...)" value={integrationForm.provider} onChange={(e) => setIntegrationForm({ ...integrationForm, provider: e.target.value })} />
              <input required className={inputClass} placeholder="Connection name" value={integrationForm.name} onChange={(e) => setIntegrationForm({ ...integrationForm, name: e.target.value })} />
              <input className={inputClass} placeholder="Server secret reference" value={integrationForm.secret_ref} onChange={(e) => setIntegrationForm({ ...integrationForm, secret_ref: e.target.value })} />
              <textarea className="min-h-28 rounded-md border border-line bg-bg p-2 font-mono text-xs" value={integrationForm.config} onChange={(e) => setIntegrationForm({ ...integrationForm, config: e.target.value })} aria-label="Provider configuration JSON" />
              <label className="flex gap-2 text-xs"><input type="checkbox" checked={integrationForm.enabled} onChange={(e) => setIntegrationForm({ ...integrationForm, enabled: e.target.checked })} />Enable after saving</label>
              <p className="text-xs text-faint">Passwords, tokens and API keys must live in the server secret manager. Enter only a reference here.</p>
              <Button type="submit" variant="solid" tone="brand" disabled={busy}>Add integration</Button>
            </form></Panel>
            <Panel title="Connected accounts">{integrations.length ? <table className="w-full text-base"><thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-2">Name</th><th>Provider</th><th>Secret reference</th><th>Status</th><th>Actions</th></tr></thead><tbody>{integrations.map((item) => <tr key={item.id} className="border-b border-line/60"><td className="p-2">{item.name}</td><td><code>{item.provider}</code></td><td><code className="text-xs">{item.secret_ref || 'Not configured'}</code></td><td><Badge tone={item.is_enabled ? 'ok' : 'neutral'}>{item.status}</Badge></td><td><div className="flex gap-1"><Button size="sm" onClick={() => void act(() => api.patch(`/admin/config/integrations/${item.id}`, { is_enabled: !item.is_enabled }), 'Integration updated')}>{item.is_enabled ? 'Disable' : 'Enable'}</Button>{catalog?.can_purge ? <Button size="sm" tone="danger" onClick={() => purge(`/admin/config/integrations/${item.id}/purge`, item.name)}>Purge</Button> : null}</div></td></tr>)}</tbody></table> : <EmptyState message="No provider accounts connected" />}</Panel>
          </div>
        ) : null}

        {section === 'branding' ? (
          <Panel title="Organization identity" className="mx-auto max-w-3xl" bodyClassName="p-4"><form className="grid gap-3 sm:grid-cols-2" onSubmit={(event) => { event.preventDefault(); void act(() => api.patch('/admin/branding', branding), 'Branding updated. Reload to apply the browser title.'); }}>
            {([['product_name', 'Product name'], ['company_name', 'Company name'], ['browser_title', 'Browser title'], ['logo_url', 'Logo URL'], ['support_email', 'Support email'], ['legal_notice', 'Legal notice']] as const).map(([key, label]) => <label key={key} className={key === 'legal_notice' ? 'sm:col-span-2' : ''}><span className="mb-1 block text-xs text-faint">{label}</span><input className={`${inputClass} w-full`} value={branding[key]} onChange={(e) => setBranding({ ...branding, [key]: e.target.value })} /></label>)}
            {([['primary_color', 'Primary colour'], ['accent_color', 'Accent colour']] as const).map(([key, label]) => <label key={key}><span className="mb-1 block text-xs text-faint">{label}</span><div className="flex gap-2"><input type="color" value={branding[key]} onChange={(e) => setBranding({ ...branding, [key]: e.target.value })} /><input className={`${inputClass} flex-1`} value={branding[key]} onChange={(e) => setBranding({ ...branding, [key]: e.target.value })} /></div></label>)}
            <div className="sm:col-span-2"><Button type="submit" variant="solid" tone="brand" disabled={busy}>Save branding</Button></div>
          </form></Panel>
        ) : null}

        {section === 'queues' ? (
          <QueueManager busy={busy} act={act} />
        ) : null}

        {section === 'ivr' ? (
          <IvrManager busy={busy} act={act} />
        ) : null}
      </main>
    </div>
  );
}

type ChartKind = 'bar' | 'line' | 'donut';
type ActivityFilter = 'all' | 'lead' | 'inbound' | 'outbound';
type MetricKey = keyof Pick<
  TrendRow,
  'calls' | 'handled' | 'abandoned' | 'talk_seconds' | 'avg_talk_seconds' | 'avg_hold_seconds' | 'aht_seconds'
>;

const metricOptions: { key: MetricKey; label: string; unit: 'count' | 'seconds' }[] = [
  { key: 'calls', label: 'All calls', unit: 'count' },
  { key: 'handled', label: 'Answered calls', unit: 'count' },
  { key: 'abandoned', label: 'Abandoned calls', unit: 'count' },
  { key: 'talk_seconds', label: 'Total talk time', unit: 'seconds' },
  { key: 'avg_talk_seconds', label: 'Average talk time', unit: 'seconds' },
  { key: 'avg_hold_seconds', label: 'Average hold time', unit: 'seconds' },
  { key: 'aht_seconds', label: 'Average handle time', unit: 'seconds' },
];

const localDate = (date: Date) => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const formatDuration = (seconds: number | null | undefined) => {
  if (seconds == null) return '—';
  const rounded = Math.max(0, Math.round(seconds));
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remaining = rounded % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`
    : `${minutes}:${String(remaining).padStart(2, '0')}`;
};

const formatMetric = (value: number | null | undefined, unit: 'count' | 'seconds' | 'percent') => {
  if (value == null) return '—';
  if (unit === 'seconds') return formatDuration(value);
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  return Math.round(value).toLocaleString();
};

function OverviewDashboard({ catalog }: { catalog: Catalog | null }) {
  const initialEnd = useMemo(() => localDate(new Date()), []);
  const initialStart = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return localDate(date);
  }, []);
  const timezone = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
    catch { return 'UTC'; }
  }, []);

  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);
  const [agentId, setAgentId] = useState('');
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const [metric, setMetric] = useState<MetricKey>('calls');
  const [chartKind, setChartKind] = useState<ChartKind>('bar');
  const [summary, setSummary] = useState<OverviewSummary | null>(null);
  const [trend, setTrend] = useState<TrendRow[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [agents, setAgents] = useState<OverviewUser[]>([]);
  const [capacity, setCapacity] = useState<CapacityInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams({ start, end, timezone });
    if (agentId) query.set('agent_id', agentId);
    try {
      const [nextSummary, nextTrend, nextActivity, nextUsers, nextCapacity] = await Promise.all([
        api.get<OverviewSummary>(`/reports/summary?${query}`),
        api.get<TrendResponse>(`/reports/trend?${query}`),
        api.get<ActivityResponse>(`/reports/activity?${query}&limit=150`),
        api.get<OverviewUser[]>('/admin/users'),
        api.get<CapacityInfo>('/admin/capacity'),
      ]);
      setSummary(nextSummary);
      setTrend(nextTrend.rows);
      setActivity(nextActivity.rows);
      setAgents(nextUsers.filter((user) => user.agent_state !== null));
      setCapacity(nextCapacity);
      setLoadError(null);
    } catch (caught) {
      setLoadError(message(caught));
    } finally {
      setLoading(false);
    }
  }, [agentId, end, start, timezone]);

  useEffect(() => { void load(); }, [load]);

  const rows = activity.filter((row) => {
    if (activityFilter === 'all') return true;
    if (activityFilter === 'lead') return row.kind === 'lead';
    return row.kind === 'call' && row.direction === activityFilter;
  });
  const metrics = summary?.metrics ?? {};
  const activeMetric = metricOptions.find((item) => item.key === metric) ?? metricOptions[0];

  const setDateRange = (days: number) => {
    const rangeEnd = new Date();
    const rangeStart = new Date();
    rangeStart.setDate(rangeEnd.getDate() - (days - 1));
    setStart(localDate(rangeStart));
    setEnd(localDate(rangeEnd));
  };

  return (
    <div className="space-y-3">
      <Panel title="Overview filters" bodyClassName="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-36 flex-1 text-xs text-faint">From
            <input className={`${inputClass} mt-1 w-full`} type="date" value={start} max={end} onChange={(event) => setStart(event.target.value)} />
          </label>
          <label className="min-w-36 flex-1 text-xs text-faint">To
            <input className={`${inputClass} mt-1 w-full`} type="date" value={end} min={start} max={initialEnd} onChange={(event) => setEnd(event.target.value)} />
          </label>
          <label className="min-w-52 flex-[1.4] text-xs text-faint">Agent
            <select className={`${inputClass} mt-1 w-full`} value={agentId} onChange={(event) => setAgentId(event.target.value)}>
              <option value="">All Agents</option>
              {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.full_name}</option>)}
            </select>
          </label>
          <div className="flex gap-1">
            <Button size="sm" onClick={() => setDateRange(1)}>Today</Button>
            <Button size="sm" onClick={() => setDateRange(7)}>7 days</Button>
            <Button size="sm" onClick={() => setDateRange(30)}>30 days</Button>
            <Button size="sm" variant="solid" tone="brand" disabled={loading} onClick={() => void load()}>Refresh</Button>
            <Button size="sm" onClick={() => void download(`/reports/export?report=summary&start=${start}&end=${end}&timezone=${timezone}&file_format=csv`, 'konnextions-summary.csv')}>Export CSV</Button>
            <Button size="sm" onClick={() => void download(`/reports/export?report=agents&start=${start}&end=${end}&timezone=${timezone}&file_format=xlsx`, 'konnextions-agents.xlsx')}>Export XLSX</Button>
          </div>
          <span className="ml-auto pb-1 text-xs text-faint">{timezone}</span>
        </div>
      </Panel>

      {loadError ? <div className="border border-danger/30 bg-danger-soft p-3 text-xs text-danger">{loadError}</div> : null}

      {capacity ? (
        <Panel title="License capacity" bodyClassName="p-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Plan</div>
              <div className="mt-1 font-medium capitalize">{capacity.plan}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Status</div>
              <Badge tone={capacity.status === 'active' ? 'ok' : capacity.status === 'trialing' ? 'info' : 'danger'}>{capacity.status}</Badge>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Licensed seats</div>
              <div className="knx-num mt-1 text-xl font-semibold">{capacity.licensed_seats}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Active users</div>
              <div className="knx-num mt-1 text-xl font-semibold">{capacity.active_users}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Available</div>
              <div className={`knx-num mt-1 text-xl font-semibold ${capacity.available_seats <= 2 ? 'text-danger' : ''}`}>{capacity.available_seats}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Utilization</div>
              <div className="mt-1 flex items-center gap-2">
                <div className="h-2 flex-1 overflow-hidden rounded-sm bg-surface-2">
                  <div className={`h-full transition-all ${capacity.utilization_pct > 90 ? 'bg-danger' : capacity.utilization_pct > 70 ? 'bg-warning' : 'bg-ok'}`} style={{ width: `${Math.min(100, capacity.utilization_pct)}%` } as CSSProperties} />
                </div>
                <span className="knx-num text-sm font-medium">{capacity.utilization_pct}%</span>
              </div>
              <div className="knx-num mt-1 text-xs text-faint">Max {capacity.concurrent_call_limit} concurrent calls</div>
            </div>
          </div>
        </Panel>
      ) : null}

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-8">
        <MetricCard label="Leads" value={catalog?.counts.leads ?? 0} />
        <MetricCard label="Calls" value={metrics.calls_total} />
        <MetricCard label="Answered" value={metrics.calls_handled} />
        <MetricCard label="Abandoned" value={metrics.calls_abandoned} />
        <MetricCard label="Avg talk" value={metrics.avg_talk_seconds} unit="seconds" />
        <MetricCard label="Avg hold" value={metrics.avg_hold_seconds} unit="seconds" />
        <MetricCard label="Service level" value={metrics.service_level_pct} unit="percent" />
        <MetricCard label="Connect rate" value={metrics.connect_rate_pct} unit="percent" />
      </div>

      <div className="grid min-h-[520px] gap-3 xl:grid-cols-[minmax(520px,0.95fr)_minmax(620px,1.35fr)]">
        <Panel
          title="Lead & call activity"
          bodyClassName="min-h-0"
          actions={(
            <select className={inputClass} value={activityFilter} onChange={(event) => setActivityFilter(event.target.value as ActivityFilter)} aria-label="Activity type">
              <option value="all">All activity</option>
              <option value="lead">Leads</option>
              <option value="outbound">Outbound calls</option>
              <option value="inbound">Inbound calls</option>
            </select>
          )}
        >
          <div className="max-h-[490px] overflow-auto">
            {rows.length ? (
              <table className="w-full text-left text-base">
                <thead className="sticky top-0 z-10 border-b border-line bg-surface text-xs uppercase text-faint">
                  <tr><th className="p-2">Date & time</th><th>Lead / caller</th><th>Type</th><th>Agent</th><th>Status</th></tr>
                </thead>
                <tbody>{rows.map((row) => (
                  <tr key={`${row.kind}-${row.id}`} className="border-b border-line/60 hover:bg-surface-2">
                    <td className="whitespace-nowrap p-2 align-top knx-num text-xs">{new Date(row.occurred_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</td>
                    <td className="max-w-48 align-top"><div className="truncate font-medium">{row.name}</div><div className="knx-num truncate text-xs text-faint">{row.phone || row.source || 'No phone'}</div></td>
                    <td className="align-top"><Badge tone={row.kind === 'lead' ? 'brand' : row.direction === 'inbound' ? 'info' : 'neutral'}>{row.kind === 'lead' ? 'Lead' : row.direction}</Badge></td>
                    <td className="max-w-36 truncate align-top">{row.agent_name}</td>
                    <td className="align-top"><div className="capitalize">{row.answered === true ? 'Answered' : row.status.replaceAll('_', ' ')}</div>{row.kind === 'call' ? <div className="knx-num text-xs text-faint">{formatDuration(row.duration_seconds)}</div> : null}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <EmptyState message="No activity in this range" hint="Change the dates, Agent, or activity filter." />}
          </div>
        </Panel>

        <Panel title="Performance chart" bodyClassName="p-3">
          <div className="mb-3 flex flex-wrap items-end gap-2 border-b border-line pb-3">
            <label className="min-w-56 flex-1 text-xs text-faint">Metric
              <select className={`${inputClass} mt-1 w-full`} value={metric} onChange={(event) => setMetric(event.target.value as MetricKey)}>
                {metricOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select>
            </label>
            <label className="min-w-40 text-xs text-faint">Chart
              <select className={`${inputClass} mt-1 w-full`} value={chartKind} onChange={(event) => setChartKind(event.target.value as ChartKind)}>
                <option value="bar">Bar graph</option>
                <option value="line">Line graph</option>
                <option value="donut">Pie / donut</option>
              </select>
            </label>
            <div className="min-w-36 border-l border-line pl-3">
              <div className="text-xs text-faint">Period total / average</div>
              <div className="knx-num text-2xl font-semibold">
                {formatMetric(
                  metric === 'calls' ? metrics.calls_total
                    : metric === 'handled' ? metrics.calls_handled
                      : metric === 'abandoned' ? metrics.calls_abandoned
                        : metric === 'talk_seconds' ? trend.reduce((sum, row) => sum + row.talk_seconds, 0)
                          : metrics[metric],
                  activeMetric.unit,
                )}
              </div>
            </div>
          </div>
          {loading ? <LoadingState label="Loading overview analytics" /> : <OperationalChart rows={trend} metric={activeMetric} kind={chartKind} />}
          <p className="mt-3 text-xs text-faint">Every value comes from persisted call records for the selected dates and Agent. Empty days remain visible as zero-volume days.</p>
        </Panel>
      </div>
    </div>
  );
}

function MetricCard({ label, value, unit = 'count' }: { label: string; value: number | null | undefined; unit?: 'count' | 'seconds' | 'percent' }) {
  return (
    <div className="border border-line bg-surface p-3 shadow-sm">
      <div className="knx-num text-xl font-semibold">{formatMetric(value, unit)}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-faint">{label}</div>
    </div>
  );
}

function OperationalChart({ rows, metric, kind }: { rows: TrendRow[]; metric: (typeof metricOptions)[number]; kind: ChartKind }) {
  const values = rows.map((row) => Number(row[metric.key] ?? 0));
  const dataMax = Math.max(...values, 0);
  const scaleMax = Math.max(dataMax, 1);
  const total = values.reduce((sum, value) => sum + value, 0);
  const palette = ['#6D5EF5', '#A3E635', '#14B8A6', '#F59E0B', '#EC4899', '#38BDF8', '#8B5CF6'];
  const points = values.map((value, index) => {
    const x = rows.length <= 1 ? 300 : 34 + (index / (rows.length - 1)) * 532;
    const y = 188 - (value / scaleMax) * 154;
    return { x, y, value, date: rows[index]?.date ?? '' };
  });

  if (!rows.length) return <EmptyState message="No chart data" hint="Choose a wider date range." />;

  if (kind === 'donut') {
    let cursor = 0;
    const gradient = total
      ? values.map((value, index) => {
        const start = cursor;
        cursor += (value / total) * 100;
        return `${palette[index % palette.length]} ${start}% ${cursor}%`;
      }).join(', ')
      : '#E5E7EB 0 100%';
    return (
      <div className="grid min-h-[360px] place-items-center gap-5 md:grid-cols-[300px_1fr]">
        <div className="grid size-64 place-items-center rounded-full" style={{ background: `conic-gradient(${gradient})` } as CSSProperties} role="img" aria-label={`${metric.label} split by day`}>
          <div className="grid size-36 place-items-center rounded-full border border-line bg-surface text-center shadow-sm"><div><strong className="knx-num block text-xl">{formatMetric(total, metric.unit)}</strong><span className="text-xs text-faint">{metric.label}</span></div></div>
        </div>
        <div className="grid w-full grid-cols-2 gap-x-4 gap-y-2 text-xs">
          {points.map((point, index) => <div key={point.date} className="flex items-center gap-2"><i className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: palette[index % palette.length] }} /><span className="text-faint">{new Date(`${point.date}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span><strong className="ml-auto knx-num">{formatMetric(point.value, metric.unit)}</strong></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[360px]">
      <svg className="h-[330px] w-full" viewBox="0 0 600 220" role="img" aria-label={`${kind} chart of ${metric.label}`} preserveAspectRatio="none">
        {[0, 1, 2, 3, 4].map((line) => <line key={line} x1="34" x2="566" y1={34 + line * 38.5} y2={34 + line * 38.5} stroke="currentColor" className="text-line" strokeWidth="1" />)}
        {kind === 'bar' ? points.map((point, index) => {
          const width = Math.max(5, Math.min(34, 480 / Math.max(points.length, 1)));
          return <rect key={point.date} x={point.x - width / 2} y={point.y} width={width} height={188 - point.y} rx="3" fill={index % 2 ? '#6D5EF5' : '#A3E635'} />;
        }) : (
          <><polyline points={points.map((point) => `${point.x},${point.y}`).join(' ')} fill="none" stroke="#6D5EF5" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />{points.map((point) => <circle key={point.date} cx={point.x} cy={point.y} r="4" fill="#A3E635" stroke="#6D5EF5" strokeWidth="2" />)}</>
        )}
        {points.map((point, index) => index % Math.max(1, Math.ceil(points.length / 7)) === 0 ? <text key={`label-${point.date}`} x={point.x} y="210" textAnchor="middle" fontSize="10" fill="currentColor" className="text-faint">{point.date.slice(5)}</text> : null)}
      </svg>
      <div className="-mt-2 flex items-center justify-between text-xs text-faint"><span>0</span><span>Peak {formatMetric(dataMax, metric.unit)}</span></div>
    </div>
  );
}


// ──────────────────────────── IVR Manager ────────────────────────────

interface IvrFlow {
  id: string;
  slug: string;
  name: string;
  version: number;
  languages: string[];
  is_published: boolean;
  published_at: string | null;
  validation_errors: { code: string; message: string; severity: string }[];
  notes: string;
  definition?: IvrDefinition;
}

interface IvrDefinition {
  start: string;
  nodes: Record<string, IvrNode>;
}

interface IvrNode {
  type: string;
  audio_asset?: string;
  next?: string;
  options?: Record<string, string>;
  timeout?: string;
  invalid?: string;
  max_retries?: number;
  queue?: string;
}

interface AudioAsset {
  id: string;
  slug: string;
  name: string;
  kind: string;
  language: string;
  duration_ms: number;
  is_generated: boolean;
  has_file: boolean;
}

interface PhoneNumber {
  id: string;
  e164: string;
  inbound_ivr_flow_id: string | null;
}

interface DryRunStep {
  node_id: string;
  node_type: string;
  selected: string | null;
  next_node_id: string | null;
}

interface DryRunResult {
  steps: DryRunStep[];
  final_node_id: string | null;
  outcome: string;
}

interface QueueRow {
  id: string;
  name: string;
  slug: string;
  description: string;
  channel: string;
  strategy: string;
  priority: number;
  service_level_seconds: number;
  service_level_target_pct: number;
  max_wait_seconds: number;
  max_queue_depth: number;
  ring_timeout_seconds: number;
  wrap_up_seconds: number;
  announce_position: boolean;
  announce_wait_time: boolean;
  callback_enabled: boolean;
  voicemail_enabled: boolean;
  disaster_mode: boolean;
  after_hours_destination: string | null;
  overflow_queue_id: string | null;
  is_active: boolean;
  member_count: number;
}

interface StrategyOption {
  value: string;
  label: string;
  description: string;
}

function QueueManager({ busy, act }: { busy: boolean; act: (action: () => Promise<unknown>, success: string) => void }) {
  const [queues, setQueues] = useState<QueueRow[]>([]);
  const [strategies, setStrategies] = useState<StrategyOption[]>([]);
  const [selected, setSelected] = useState<QueueRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '', slug: '', description: '', strategy: 'longest_idle', channel: 'voice',
    service_level_seconds: '20', service_level_target_pct: '80', max_wait_seconds: '600',
    max_queue_depth: '100', ring_timeout_seconds: '20', wrap_up_seconds: '30',
    announce_position: true, announce_wait_time: true, callback_enabled: true, voicemail_enabled: true,
  });

  const load = useCallback(async () => {
    const [nextQueues, nextStrategies] = await Promise.all([
      api.get<QueueRow[]>('/admin/queues').catch(() => []),
      api.get<StrategyOption[]>('/admin/routing-strategies').catch(() => []),
    ]);
    setQueues(nextQueues);
    setStrategies(nextStrategies);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const createQueue = (event: FormEvent) => {
    event.preventDefault();
    void act(async () => {
      await api.post('/admin/queues', {
        name: form.name, slug: form.slug, description: form.description,
        strategy: form.strategy, channel: form.channel,
        service_level_seconds: Number(form.service_level_seconds),
        service_level_target_pct: Number(form.service_level_target_pct),
        max_wait_seconds: Number(form.max_wait_seconds),
        max_queue_depth: Number(form.max_queue_depth),
        ring_timeout_seconds: Number(form.ring_timeout_seconds),
        wrap_up_seconds: Number(form.wrap_up_seconds),
        announce_position: form.announce_position,
        announce_wait_time: form.announce_wait_time,
        callback_enabled: form.callback_enabled,
        voicemail_enabled: form.voicemail_enabled,
      });
      setCreating(false);
      setForm({ name: '', slug: '', description: '', strategy: 'longest_idle', channel: 'voice',
        service_level_seconds: '20', service_level_target_pct: '80', max_wait_seconds: '600',
        max_queue_depth: '100', ring_timeout_seconds: '20', wrap_up_seconds: '30',
        announce_position: true, announce_wait_time: true, callback_enabled: true, voicemail_enabled: true });
      await load();
    }, 'Queue created');
  };

  const updateStrategy = (queueId: string, strategy: string) => {
    void act(async () => {
      await api.patch(`/admin/queues/${queueId}`, { strategy });
      await load();
      if (selected?.id === queueId) setSelected({ ...selected, strategy });
    }, `Routing strategy updated to ${strategy.replace(/_/g, ' ')}`);
  };

  const toggleActive = (queue: QueueRow) => {
    void act(async () => {
      await api.patch(`/admin/queues/${queue.id}`, { is_active: !queue.is_active });
      await load();
    }, queue.is_active ? 'Queue deactivated' : 'Queue activated');
  };

  const updateField = (queueId: string, field: string, value: unknown) => {
    void act(async () => {
      await api.patch(`/admin/queues/${queueId}`, { [field]: value });
      await load();
    }, `Queue ${field.replace(/_/g, ' ')} updated`);
  };

  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_380px]">
      <div className="space-y-3">
        <Panel title="Queues" actions={
          <Button size="sm" variant="solid" tone="brand" onClick={() => setCreating(!creating)}>
            {creating ? 'Cancel' : '+ New queue'}
          </Button>
        }>
          {creating ? (
            <form className="grid gap-2 border-b border-line p-3" onSubmit={createQueue}>
              <div className="grid grid-cols-2 gap-2">
                <input required className={inputClass} placeholder="Queue name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_') })} />
                <input required className={inputClass} placeholder="slug" value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
              <input className={inputClass} placeholder="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <select className={inputClass} value={form.strategy} onChange={(e) => setForm({ ...form, strategy: e.target.value })}>
                  {strategies.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <select className={inputClass} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                  {['voice', 'sms', 'whatsapp', 'email', 'webchat'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <Button type="submit" variant="solid" tone="brand" disabled={busy}>Create queue</Button>
            </form>
          ) : null}
          {queues.length ? (
            <table className="w-full text-base">
              <thead><tr className="border-b border-line text-left text-xs text-faint">
                <th className="p-2">Queue</th><th>Strategy</th><th>Members</th><th>SL</th><th>State</th><th>Actions</th>
              </tr></thead>
              <tbody>{queues.map((q) => (
                <tr key={q.id} className={`border-b border-line/60 cursor-pointer ${selected?.id === q.id ? 'bg-brand-soft' : ''}`}
                  onClick={() => setSelected(q)}>
                  <td className="p-2">
                    <div className="font-medium">{q.name}</div>
                    <div className="text-xs text-faint">{q.slug} · {q.channel}</div>
                  </td>
                  <td>
                    <select className="h-7 rounded border border-line bg-bg px-1 text-xs"
                      value={q.strategy} onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateStrategy(q.id, e.target.value)}>
                      {strategies.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="text-center">{q.member_count}</td>
                  <td className="text-xs">{q.service_level_target_pct}% / {q.service_level_seconds}s</td>
                  <td><Badge tone={q.is_active ? 'ok' : 'neutral'}>{q.is_active ? 'active' : 'off'}</Badge></td>
                  <td>
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); toggleActive(q); }}>
                      {q.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          ) : <EmptyState message="No queues configured" hint="Create a queue to start routing inbound calls." />}
        </Panel>

        {strategies.length > 0 ? (
          <Panel title="Routing strategies reference">
            <div className="grid gap-2 p-3 sm:grid-cols-2">
              {strategies.map((s) => (
                <div key={s.value} className="rounded-md border border-line p-2">
                  <div className="text-sm font-medium">{s.label}</div>
                  <div className="text-xs text-muted">{s.description}</div>
                </div>
              ))}
            </div>
          </Panel>
        ) : null}
      </div>

      {selected ? (
        <Panel title={`${selected.name} — settings`} bodyClassName="space-y-2 p-3">
          <div className="text-xs text-faint">
            Slug: <code>{selected.slug}</code> · Channel: {selected.channel} · Members: {selected.member_count}
          </div>
          <label className="block"><span className="text-xs text-faint">Routing strategy</span>
            <select className={`${inputClass} w-full`} value={selected.strategy}
              onChange={(e) => updateStrategy(selected.id, e.target.value)}>
              {strategies.map((s) => <option key={s.value} value={s.value}>{s.label} — {s.description}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label><span className="text-xs text-faint">SL seconds</span>
              <input type="number" className={`${inputClass} w-full`} value={selected.service_level_seconds}
                onChange={(e) => updateField(selected.id, 'service_level_seconds', Number(e.target.value))} />
            </label>
            <label><span className="text-xs text-faint">SL target %</span>
              <input type="number" className={`${inputClass} w-full`} value={selected.service_level_target_pct}
                onChange={(e) => updateField(selected.id, 'service_level_target_pct', Number(e.target.value))} />
            </label>
            <label><span className="text-xs text-faint">Max wait (s)</span>
              <input type="number" className={`${inputClass} w-full`} value={selected.max_wait_seconds}
                onChange={(e) => updateField(selected.id, 'max_wait_seconds', Number(e.target.value))} />
            </label>
            <label><span className="text-xs text-faint">Max depth</span>
              <input type="number" className={`${inputClass} w-full`} value={selected.max_queue_depth}
                onChange={(e) => updateField(selected.id, 'max_queue_depth', Number(e.target.value))} />
            </label>
            <label><span className="text-xs text-faint">Ring timeout (s)</span>
              <input type="number" className={`${inputClass} w-full`} value={selected.ring_timeout_seconds}
                onChange={(e) => updateField(selected.id, 'ring_timeout_seconds', Number(e.target.value))} />
            </label>
            <label><span className="text-xs text-faint">Wrap-up (s)</span>
              <input type="number" className={`${inputClass} w-full`} value={selected.wrap_up_seconds}
                onChange={(e) => updateField(selected.id, 'wrap_up_seconds', Number(e.target.value))} />
            </label>
          </div>
          {([
            ['announce_position', 'Announce queue position'],
            ['announce_wait_time', 'Announce estimated wait'],
            ['callback_enabled', 'Allow queue callbacks'],
            ['voicemail_enabled', 'Voicemail when unavailable'],
            ['disaster_mode', 'Disaster / failover mode'],
          ] as const).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={selected[key]}
                onChange={(e) => updateField(selected.id, key, e.target.checked)} />
              {label}
            </label>
          ))}
          <label><span className="text-xs text-faint">After-hours destination</span>
            <input className={`${inputClass} w-full`} placeholder="Queue ID, phone, or voicemail"
              value={selected.after_hours_destination ?? ''}
              onBlur={(e) => updateField(selected.id, 'after_hours_destination', e.target.value || '')} />
          </label>
        </Panel>
      ) : (
        <Panel title="Queue settings" bodyClassName="p-3">
          <EmptyState message="Select a queue" hint="Click a queue row to edit its routing and configuration." />
        </Panel>
      )}
    </div>
  );
}


function IvrManager({ busy, act }: { busy: boolean; act: (action: () => Promise<unknown>, success: string) => void }) {
  const [flows, setFlows] = useState<IvrFlow[]>([]);
  const [assets, setAssets] = useState<AudioAsset[]>([]);
  const [numbers, setNumbers] = useState<PhoneNumber[]>([]);
  const [queues, setQueues] = useState<QueueRow[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<IvrFlow | null>(null);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [dryRunInputs, setDryRunInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [subSection, setSubSection] = useState<'flows' | 'audio' | 'assign'>('flows');

  const [flowForm, setFlowForm] = useState({ name: '', slug: '' });
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioForm, setAudioForm] = useState({ name: '', slug: '', kind: 'prompt', language: 'en' });
  const [nodeForm, setNodeForm] = useState({ id: '', type: 'menu', audio_asset: '', queue: '', next: '', options: '{"1": "", "2": ""}' });
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [editingNode, setEditingNode] = useState<IvrNode | null>(null);

  const slugify = (value: string) => value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [nextFlows, nextAssets, nextQueues, nextNumbers] = await Promise.all([
        api.get<IvrFlow[]>('/ivr/flows'),
        api.get<AudioAsset[]>('/ivr/audio-assets'),
        api.get<QueueRow[]>('/admin/queues').catch(() => [] as QueueRow[]),
        api.get<PhoneNumber[]>('/admin/phone-numbers').catch(() => [] as PhoneNumber[]),
      ]);
      setFlows(nextFlows);
      setAssets(nextAssets);
      setQueues(nextQueues);
      setNumbers(nextNumbers);
    } catch { /* handled by parent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const loadFlow = async (flowId: string) => {
    const flow = await api.get<IvrFlow>(`/ivr/flows/${flowId}`);
    setSelectedFlow(flow);
    setDryRunInputs({});
    setDryRunResult(null);
  };

  const createFlow = (event: FormEvent) => {
    event.preventDefault();
    const slug = slugify(flowForm.slug);
    if (!slug) return;
    act(async () => {
      await api.post(`/ivr/flows?name=${encodeURIComponent(flowForm.name.trim())}&slug=${encodeURIComponent(slug)}`);
      setFlowForm({ name: '', slug: '' });
      await refresh();
    }, 'IVR flow created with default template');
  };

  const publishFlow = (flowId: string) => {
    act(async () => {
      await api.post(`/ivr/flows/${flowId}/publish`);
      await refresh();
      if (selectedFlow?.id === flowId) await loadFlow(flowId);
    }, 'IVR flow published');
  };

  const runDryRun = async (flowId: string) => {
    const result = await api.post<DryRunResult>(`/ivr/flows/${flowId}/dry-run`, { inputs: dryRunInputs });
    setDryRunResult(result);
  };

  const uploadAudio = (event: FormEvent) => {
    event.preventDefault();
    if (!audioFile) return;
    const slug = slugify(audioForm.slug || audioForm.name);
    if (!slug) return;
    const data = new FormData();
    data.append('file', audioFile);
    act(async () => {
      await api.postForm(`/ivr/audio-assets?name=${encodeURIComponent(audioForm.name.trim())}&slug=${encodeURIComponent(slug)}&kind=${audioForm.kind}&language=${audioForm.language}`, data);
      setAudioForm({ name: '', slug: '', kind: 'prompt', language: 'en' });
      setAudioFile(null);
      await refresh();
    }, 'Audio asset uploaded');
  };

  const deleteAudio = (assetId: string) => {
    if (!window.confirm('Delete this audio asset?')) return;
    act(async () => { await api.del(`/ivr/audio-assets/${assetId}`); await refresh(); }, 'Audio asset deleted');
  };

  const assignFlow = (flowId: string, numberId: string) => {
    act(async () => {
      await api.post(`/ivr/flows/${flowId}/assign?phone_number_id=${numberId}`);
      await refresh();
    }, 'IVR flow assigned to phone number');
  };

  const addNode = () => {
    if (!selectedFlow?.definition || !nodeForm.id) return;
    const def = { ...selectedFlow.definition, nodes: { ...selectedFlow.definition.nodes } };
    const node: IvrNode = { type: nodeForm.type };
    if (nodeForm.audio_asset) node.audio_asset = nodeForm.audio_asset;
    if (nodeForm.type === 'route') node.queue = nodeForm.queue;
    else if (nodeForm.type === 'menu') {
      try { node.options = JSON.parse(nodeForm.options) as Record<string, string>; } catch { /* */ }
      node.timeout = nodeForm.id;
      node.invalid = nodeForm.id;
    } else if (nodeForm.type === 'play') node.next = nodeForm.next;
    def.nodes[nodeForm.id] = node;
    act(async () => {
      await api.put(`/ivr/flows/${selectedFlow.id}`, { definition: def });
      await loadFlow(selectedFlow.id);
    }, `Node "${nodeForm.id}" added`);
  };

  const removeNode = (nodeId: string) => {
    if (!selectedFlow?.definition) return;
    if (!window.confirm(`Remove node "${nodeId}"?`)) return;
    const def = { ...selectedFlow.definition, nodes: { ...selectedFlow.definition.nodes } };
    delete def.nodes[nodeId];
    act(async () => {
      await api.put(`/ivr/flows/${selectedFlow.id}`, { definition: def });
      await loadFlow(selectedFlow.id);
    }, `Node "${nodeId}" removed`);
  };

  const startEditNode = (nodeId: string, node: IvrNode) => {
    setEditingNodeId(nodeId);
    setEditingNode({ ...node, options: node.options ? { ...node.options } : undefined });
  };

  const saveNode = () => {
    if (!selectedFlow?.definition || !editingNodeId || !editingNode) return;
    const def = { ...selectedFlow.definition, nodes: { ...selectedFlow.definition.nodes, [editingNodeId]: editingNode } };
    act(async () => {
      await api.put(`/ivr/flows/${selectedFlow.id}`, { definition: def });
      setEditingNodeId(null);
      setEditingNode(null);
      await loadFlow(selectedFlow.id);
    }, `Node "${editingNodeId}" updated`);
  };

  const addMenuOption = () => {
    if (!editingNode) return;
    const options = { ...(editingNode.options ?? {}) };
    let key = '1';
    while (options[key]) key = String(Number(key) + 1);
    options[key] = '';
    setEditingNode({ ...editingNode, options });
  };

  const removeMenuOption = (key: string) => {
    if (!editingNode?.options) return;
    const options = { ...editingNode.options };
    delete options[key];
    setEditingNode({ ...editingNode, options });
  };

  if (loading) return <LoadingState label="Loading IVR configuration" />;
  const dryRunNodes = selectedFlow?.definition?.nodes ?? {};

  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        <Button size="sm" variant={subSection === 'flows' ? 'solid' : 'ghost'} tone="brand" onClick={() => setSubSection('flows')}>IVR flows</Button>
        <Button size="sm" variant={subSection === 'audio' ? 'solid' : 'ghost'} tone="brand" onClick={() => setSubSection('audio')}>Audio recordings</Button>
        <Button size="sm" variant={subSection === 'assign' ? 'solid' : 'ghost'} tone="brand" onClick={() => setSubSection('assign')}>Phone number assignment</Button>
      </div>

      {subSection === 'flows' ? (
        <div className="grid gap-3 xl:grid-cols-[380px_1fr]">
          <div className="space-y-3">
            <Panel title="Create IVR flow" bodyClassName="p-3">
              <form className="grid gap-2" onSubmit={createFlow}>
                <input required className={inputClass} placeholder="Flow name (e.g. Main IVR)" value={flowForm.name} onChange={(e) => setFlowForm({ ...flowForm, name: e.target.value })} />
                <input required className={inputClass} placeholder="Slug (e.g. main-ivr)" pattern="^[a-z0-9_-]+$" value={flowForm.slug} onChange={(e) => setFlowForm({ ...flowForm, slug: slugify(e.target.value) })} />
                <Button type="submit" variant="solid" tone="brand" disabled={busy}>Create flow</Button>
              </form>
            </Panel>
            <Panel title="Existing flows">
              {flows.length ? flows.map((flow) => (
                <button key={flow.id} className={`w-full border-b border-line/60 p-2 text-left hover:bg-surface-2 ${selectedFlow?.id === flow.id ? 'bg-surface-2' : ''}`} onClick={() => void loadFlow(flow.id)}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{flow.name}</span>
                    <Badge tone={flow.is_published ? 'ok' : 'warn'}>{flow.is_published ? `v${flow.version}` : 'draft'}</Badge>
                  </div>
                  <div className="text-xs text-faint">{flow.slug} · {flow.languages.join(', ')}</div>
                </button>
              )) : <EmptyState message="No IVR flows" hint="Create one to get started." />}
            </Panel>
          </div>

          <div className="space-y-3">
            {selectedFlow ? (
              <>
                <Panel title={`${selectedFlow.name} — v${selectedFlow.version}`} actions={
                  <div className="flex gap-1">
                    <Button size="sm" variant="solid" tone="brand" onClick={() => publishFlow(selectedFlow.id)} disabled={busy || selectedFlow.is_published}>
                      {selectedFlow.is_published ? 'Published' : 'Publish'}
                    </Button>
                  </div>
                }>
                  <div className="grid gap-1 border-b border-line/60 bg-surface-2 p-3 text-xs">
                    <strong>IVR setup checklist</strong>
                    <span>{selectedFlow.definition?.start ? '✓ Start node selected' : '✗ Choose a start node'}</span>
                    <span>{Object.values(selectedFlow.definition?.nodes ?? {}).filter((node) => node.type === 'menu').every((node) => Object.keys(node.options ?? {}).length > 0) ? '✓ Menu options configured' : '✗ Add at least one option to each menu'}</span>
                    <span>{Object.values(selectedFlow.definition?.nodes ?? {}).filter((node) => node.type === 'route').every((node) => node.queue) ? '✓ Every route has a queue' : '✗ Select a queue for every route node'}</span>
                    <span className="text-faint">Edit a node below, then publish only after the checklist is complete.</span>
                  </div>
                  {selectedFlow.validation_errors.length > 0 ? (
                    <div className="border-b border-danger/30 bg-danger-soft p-2 text-xs text-danger">
                      {selectedFlow.validation_errors.map((issue, i) => <div key={i}>{issue.severity}: {issue.message} ({issue.code})</div>)}
                    </div>
                  ) : null}
                  {selectedFlow.definition ? (
                    <div className="p-3">
                      <div className="mb-2 text-xs text-faint">Start node: <code className="rounded bg-surface-3 px-1">{selectedFlow.definition.start}</code></div>
                      <table className="w-full text-base">
                        <thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-1">Node ID</th><th className="p-1">Type</th><th className="p-1">Audio</th><th className="p-1">Options / next</th><th className="p-1">Actions</th></tr></thead>
                        <tbody>
                          {Object.entries(selectedFlow.definition.nodes).map(([nodeId, node]) => (
                            <Fragment key={nodeId}>
                            <tr className="border-b border-line/60">
                              <td className="p-1"><code className="text-xs">{nodeId}</code>{nodeId === selectedFlow.definition?.start ? <Badge tone="brand" className="ml-1">start</Badge> : null}</td>
                              <td className="p-1"><Badge tone={node.type === 'route' ? 'ok' : node.type === 'menu' ? 'info' : node.type === 'hangup' ? 'danger' : 'neutral'}>{node.type}</Badge></td>
                              <td className="p-1 text-xs text-muted">{node.audio_asset || '—'}</td>
                              <td className="p-1 text-xs">
                                {node.type === 'menu' && node.options ? Object.entries(node.options).map(([key, target]) => <div key={key}>Press <strong>{key}</strong> → <code>{target}</code></div>) : null}
                                {node.type === 'route' ? <span>Queue: <code>{node.queue}</code></span> : null}
                                {node.type === 'play' && node.next ? <span>Next: <code>{node.next}</code></span> : null}
                                {node.type === 'hangup' ? <span className="text-faint">Ends call</span> : null}
                                {node.type === 'voicemail' ? <span className="text-faint">Records voicemail</span> : null}
                              </td>
                              <td className="p-1"><div className="flex gap-1"><Button size="sm" onClick={() => startEditNode(nodeId, node)}>Edit</Button><Button size="sm" tone="danger" onClick={() => removeNode(nodeId)} disabled={busy || nodeId === selectedFlow.definition?.start}>Remove</Button></div></td>
                            </tr>
                            {editingNodeId === nodeId && editingNode ? (
                              <tr className="border-b border-line/60 bg-surface-2"><td colSpan={5} className="p-3">
                                <div className="grid gap-2 md:grid-cols-2">
                                  <label className="text-xs text-faint">Node type<select className={inputClass} value={editingNode.type} onChange={(e) => setEditingNode({ ...editingNode, type: e.target.value })}><option value="menu">Menu</option><option value="play">Play audio</option><option value="route">Route to queue</option><option value="voicemail">Voicemail</option><option value="hangup">Hang up</option><option value="gather">Gather digits</option><option value="ai_handoff">AI handoff</option></select></label>
                                  <label className="text-xs text-faint">Audio prompt<select className={inputClass} value={editingNode.audio_asset ?? ''} onChange={(e) => setEditingNode({ ...editingNode, audio_asset: e.target.value || undefined })}><option value="">No audio</option>{assets.filter((asset) => asset.has_file).map((asset) => <option key={asset.id} value={asset.slug}>{asset.name}</option>)}</select></label>
                                  {editingNode.type === 'route' ? <label className="text-xs text-faint">Queue<select className={inputClass} value={editingNode.queue ?? ''} onChange={(e) => setEditingNode({ ...editingNode, queue: e.target.value })}><option value="">Select a queue</option>{queues.filter((queue) => queue.is_active).map((queue) => <option key={queue.id} value={queue.slug}>{queue.name} ({queue.slug})</option>)}</select></label> : null}
                                  {editingNode.type === 'play' ? <label className="text-xs text-faint">Next node<select className={inputClass} value={editingNode.next ?? ''} onChange={(e) => setEditingNode({ ...editingNode, next: e.target.value })}><option value="">Select next node</option>{Object.keys(selectedFlow.definition?.nodes ?? {}).map((id) => <option key={id} value={id}>{id}</option>)}</select></label> : null}
                                </div>
                                {editingNode.type === 'menu' || editingNode.type === 'gather' ? <div className="mt-2 grid gap-2"><div className="flex items-center justify-between"><strong className="text-xs">Menu options</strong><Button size="sm" onClick={addMenuOption}>Add option</Button></div>{Object.entries(editingNode.options ?? {}).map(([key, target]) => <div key={key} className="grid grid-cols-[90px_1fr_auto] gap-2"><input className={inputClass} aria-label={`Digit for option ${key}`} value={key} onChange={(e) => { const options = { ...(editingNode.options ?? {}) }; const value = options[key]; delete options[key]; options[e.target.value] = value; setEditingNode({ ...editingNode, options }); }} /><select className={inputClass} aria-label={`Target for option ${key}`} value={target} onChange={(e) => setEditingNode({ ...editingNode, options: { ...(editingNode.options ?? {}), [key]: e.target.value } })}><option value="">Select destination node</option>{Object.keys(selectedFlow.definition?.nodes ?? {}).map((id) => <option key={id} value={id}>{id}</option>)}</select><Button size="sm" tone="danger" onClick={() => removeMenuOption(key)}>Remove</Button></div>)}</div> : null}
                                <div className="mt-2 flex gap-2"><Button size="sm" variant="solid" tone="brand" onClick={saveNode} disabled={busy}>Save node</Button><Button size="sm" onClick={() => { setEditingNodeId(null); setEditingNode(null); }}>Cancel</Button></div>
                              </td></tr>
                            ) : null}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </Panel>

                <Panel title="Add node to flow" bodyClassName="p-3">
                  <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                    <input className={inputClass} placeholder="Node ID (e.g. billing)" value={nodeForm.id} onChange={(e) => setNodeForm({ ...nodeForm, id: e.target.value })} />
                    <select className={inputClass} value={nodeForm.type} onChange={(e) => setNodeForm({ ...nodeForm, type: e.target.value })}>
                      <option value="menu">Menu (press 1/2/#)</option>
                      <option value="play">Play audio</option>
                      <option value="route">Route to queue</option>
                      <option value="voicemail">Voicemail</option>
                      <option value="hangup">Hang up</option>
                      <option value="gather">Gather digits</option>
                      <option value="ai_handoff">AI handoff</option>
                    </select>
                    <select className={inputClass} value={nodeForm.audio_asset} onChange={(e) => setNodeForm({ ...nodeForm, audio_asset: e.target.value })}>
                      <option value="">No audio asset</option>
                      {assets.map((asset) => <option key={asset.id} value={asset.slug}>{asset.name} ({asset.slug})</option>)}
                    </select>
                    {nodeForm.type === 'route' ? <select className={inputClass} value={nodeForm.queue} onChange={(e) => setNodeForm({ ...nodeForm, queue: e.target.value })}><option value="">Select a queue</option>{queues.filter((queue) => queue.is_active).map((queue) => <option key={queue.id} value={queue.slug}>{queue.name} ({queue.slug})</option>)}</select> : null}
                    {nodeForm.type === 'play' ? <input className={inputClass} placeholder="Next node ID" value={nodeForm.next} onChange={(e) => setNodeForm({ ...nodeForm, next: e.target.value })} /> : null}
                    {nodeForm.type === 'menu' ? <input className={inputClass} placeholder='Options JSON: {"1":"sales"}' value={nodeForm.options} onChange={(e) => setNodeForm({ ...nodeForm, options: e.target.value })} /> : null}
                  </div>
                  <Button className="mt-2" variant="solid" tone="brand" disabled={busy || !nodeForm.id} onClick={addNode}>Add node</Button>
                </Panel>

                <Panel title="Dry-run simulator" bodyClassName="p-3">
                  <p className="mb-3 text-xs text-faint">Choose the digit a caller would press at each menu. You do not need to write JSON.</p>
                  <div className="space-y-2">
                    {Object.entries(dryRunNodes)
                      .filter(([, node]) => node.type === 'menu' || node.type === 'gather')
                      .map(([nodeId, node]) => {
                        const choices = Object.keys(node.options ?? {});
                        return (
                          <label key={nodeId} className="grid gap-1 sm:grid-cols-[1fr_180px] sm:items-center">
                            <span className="text-sm"><code>{nodeId}</code><span className="ml-2 text-xs text-faint">{node.type === 'gather' ? 'Gather digits' : 'Menu'}</span></span>
                            {choices.length ? (
                              <select className={inputClass} value={dryRunInputs[nodeId] ?? ''}
                                onChange={(e) => setDryRunInputs({ ...dryRunInputs, [nodeId]: e.target.value })}>
                                <option value="">No digit (timeout)</option>
                                {choices.map((digit) => <option key={digit} value={digit}>Press {digit}{node.options?.[digit] ? ` → ${node.options[digit]}` : ''}</option>)}
                              </select>
                            ) : (
                              <input className={inputClass} inputMode="numeric" maxLength={1} placeholder="Digit (e.g. 1)"
                                value={dryRunInputs[nodeId] ?? ''}
                                onChange={(e) => setDryRunInputs({ ...dryRunInputs, [nodeId]: e.target.value.replace(/[^0-9*#]/g, '').slice(0, 1) })} />
                            )}
                          </label>
                        );
                      })}
                    {!Object.values(dryRunNodes).some((node) => node.type === 'menu' || node.type === 'gather') ? (
                      <p className="text-xs text-faint">This flow has no menu or digit-gather nodes yet.</p>
                    ) : null}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button variant="solid" tone="brand" disabled={busy} onClick={() => void runDryRun(selectedFlow.id)}>Run simulation</Button>
                    <Button disabled={busy || !Object.keys(dryRunInputs).length} onClick={() => setDryRunInputs({})}>Clear digits</Button>
                  </div>
                  {dryRunResult ? (
                    <div className="mt-3">
                      <div className="mb-2 flex items-center gap-2">
                        <Badge tone={dryRunResult.outcome === 'route' ? 'ok' : dryRunResult.outcome === 'hangup' ? 'danger' : 'warn'}>{dryRunResult.outcome}</Badge>
                        {dryRunResult.final_node_id ? <span className="text-xs">Ended at: <code>{dryRunResult.final_node_id}</code></span> : null}
                      </div>
                      <table className="w-full text-xs">
                        <thead><tr className="border-b border-line text-faint"><th className="p-1 text-left">Step</th><th className="p-1 text-left">Node</th><th className="p-1 text-left">Type</th><th className="p-1 text-left">Input</th><th className="p-1 text-left">Next</th></tr></thead>
                        <tbody>{dryRunResult.steps.map((step, i) => (
                          <tr key={i} className="border-b border-line/60">
                            <td className="p-1">{i + 1}</td>
                            <td className="p-1"><code>{step.node_id}</code></td>
                            <td className="p-1">{step.node_type}</td>
                            <td className="p-1">{step.selected ?? '—'}</td>
                            <td className="p-1">{step.next_node_id ?? '—'}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  ) : null}
                </Panel>
              </>
            ) : <EmptyState message="Select an IVR flow" hint="Click a flow from the list to view and edit its menu structure." />}
          </div>
        </div>
      ) : null}

      {subSection === 'audio' ? (
        <div className="grid gap-3 lg:grid-cols-[400px_1fr]">
          <Panel title="Upload audio recording" bodyClassName="p-3">
            <p className="mb-3 rounded-md border border-warn/30 bg-warn-soft p-2 text-xs text-warn">
              These recordings are used by an assigned inbound IVR flow. Exotel outbound connect calls do not play an uploaded greeting; they ring the configured agent endpoint first.
            </p>
            <form className="grid gap-2" onSubmit={uploadAudio}>
              <input required className={inputClass} placeholder="Name (e.g. Welcome Greeting)" value={audioForm.name} onChange={(e) => setAudioForm({ ...audioForm, name: e.target.value })} />
              <input required className={inputClass} placeholder="Slug (e.g. welcome-greeting)" pattern="^[a-z0-9_-]+$" value={audioForm.slug} onChange={(e) => setAudioForm({ ...audioForm, slug: slugify(e.target.value) })} />
              <select className={inputClass} value={audioForm.kind} onChange={(e) => setAudioForm({ ...audioForm, kind: e.target.value })}>
                <option value="prompt">Prompt</option>
                <option value="greeting">Greeting</option>
                <option value="hold_music">Hold music</option>
                <option value="announcement">Announcement</option>
                <option value="menu_option">Menu option</option>
              </select>
              <select className={inputClass} value={audioForm.language} onChange={(e) => setAudioForm({ ...audioForm, language: e.target.value })}>
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="ar">Arabic</option>
              </select>
              <input type="file" accept=".wav,.mp3,.ogg,.opus,.aac" onChange={(e) => setAudioFile(e.target.files?.[0] ?? null)} />
              <p className="text-xs text-faint">Supported: WAV, MP3, OGG, Opus, AAC. Max 25MB per file.</p>
              <Button type="submit" variant="solid" tone="brand" disabled={busy || !audioFile}>Upload</Button>
            </form>
          </Panel>
          <Panel title="Audio library">
            {assets.length ? (
              <table className="w-full text-base">
                <thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-2">Name / slug</th><th>Kind</th><th>Language</th><th>File</th><th>Actions</th></tr></thead>
                <tbody>{assets.map((asset) => (
                  <tr key={asset.id} className="border-b border-line/60">
                    <td className="p-2"><div>{asset.name}</div><code className="text-xs text-faint">{asset.slug}</code></td>
                    <td>{asset.kind}</td>
                    <td>{asset.language}</td>
                    <td><Badge tone={asset.has_file ? 'ok' : 'warn'}>{asset.has_file ? 'Uploaded' : 'Missing'}</Badge></td>
                    <td>
                      <div className="flex gap-1">
                        {asset.has_file ? <Button size="sm" onClick={() => void getBlob(`/ivr/audio-assets/${asset.id}/media`).then((blob) => { const url = URL.createObjectURL(blob); window.open(url, '_blank'); window.setTimeout(() => URL.revokeObjectURL(url), 60_000); })}>Play</Button> : null}
                        <Button size="sm" tone="danger" onClick={() => deleteAudio(asset.id)} disabled={busy}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <EmptyState message="No audio recordings" hint="Upload your first IVR prompt or greeting." />}
          </Panel>
        </div>
      ) : null}

      {subSection === 'assign' ? (
        <Panel title="Assign IVR flow to phone numbers" bodyClassName="p-3">
          <p className="mb-3 text-xs text-faint">When a phone number has an IVR flow assigned, all inbound calls to that number will enter the IVR menu automatically.</p>
          {numbers.length ? (
            <table className="w-full text-base">
              <thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-2">Phone number</th><th>Current IVR</th><th>Assign flow</th></tr></thead>
              <tbody>{numbers.map((num) => (
                <tr key={num.id} className="border-b border-line/60">
                  <td className="knx-num p-2">{num.e164}</td>
                  <td><Badge tone={num.inbound_ivr_flow_id ? 'ok' : 'neutral'}>{num.inbound_ivr_flow_id ? 'Assigned' : 'None'}</Badge></td>
                  <td>
                    <select className={inputClass} value="" onChange={(e) => { if (e.target.value) assignFlow(e.target.value, num.id); }}>
                      <option value="">Select a published flow</option>
                      {flows.filter((f) => f.is_published).map((f) => <option key={f.id} value={f.id}>{f.name} (v{f.version})</option>)}
                    </select>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          ) : <EmptyState message="No phone numbers configured" hint="Phone numbers are provisioned by the system administrator." />}
        </Panel>
      ) : null}
    </div>
  );
}

// ──────────────────────────── Providers Manager ────────────────────────────
// Implements PROVIDER_INTEGRATION_CONTRACT.md's Admin flow against the
// app/api/v1/providers.py catalogue built in section 10.1 batches 1-2: a
// searchable, server-owned catalogue (never free text), a schema-generated
// credential form (never raw JSON), write-only secrets (never round-tripped
// back to the browser), a real connection test, and number sync or an
// honestly-labelled manual fallback. This is a SEPARATE concept from the
// "Accounts & integrations" section above — that one drives the generic,
// free-text Integration model (CRM/webhook connections); this one drives
// telephony-carrier ProviderConnection specifically, and intentionally does
// not touch or replace the other.

interface ProviderSummary {
  provider_id: string;
  display_name: string;
  docs_url: string;
  logo_key: string;
  regions: string[];
  connection_mode: string;
  capabilities: string[];
}

interface ProviderFieldOption { value: string; label: string }

interface ProviderField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  secret: boolean;
  help_text: string;
  placeholder: string;
  validation_pattern?: string;
  options?: ProviderFieldOption[];
  depends_on?: { field: string; value: string };
}

interface ProviderSchema extends ProviderSummary {
  fields: ProviderField[];
}

interface ConnectionStatus {
  provider_id: string | null;
  status: string;
  config?: Record<string, unknown>;
  secret_fields_configured?: string[];
  capabilities_granted?: string[];
  last_tested_at?: string | null;
  last_test_success?: boolean;
  last_test_code?: string;
  last_test_message?: string;
  last_numbers_synced_at?: string | null;
  disabled_reason?: string;
}

interface ConnectedNumber {
  id: string;
  e164: string;
  provider: string;
  inbound_ivr_flow_id: string | null;
}

const CAPABILITY_LABELS: Record<string, string> = {
  place_call: 'Outbound calling',
  answer: 'Inbound answering',
  hangup: 'Hang up',
  hold: 'Hold',
  mute: 'Mute',
  blind_transfer: 'Blind transfer',
  warm_transfer: 'Warm transfer',
  conference: 'Conference',
  monitor: 'Supervisor monitor',
  whisper: 'Supervisor whisper',
  barge: 'Supervisor barge',
  dtmf: 'DTMF',
  recording: 'Call recording',
  recording_pause: 'Pause recording',
  media_stream: 'Media streaming',
  tts: 'Text to speech',
  gather_digits: 'IVR digit gathering',
  voicemail: 'Voicemail',
  webhook_verification: 'Signed webhooks',
  cost_events: 'Cost reporting',
  number_purchase: 'Buy numbers',
  caller_id_inventory: 'Number inventory sync',
  answering_machine_detection: 'Answering machine detection',
};

function capabilityLabel(value: string): string {
  return CAPABILITY_LABELS[value] ?? value.replace(/_/g, ' ');
}

/** What GET /admin/capacity returns. Mirrors app.services.seats.SeatUsage. */
interface SeatCapacity {
  plan: string;
  status: string;
  licensed_seats: number;
  active_users: number;
  billable_users: number;
  free_owner_seats: number;
  free_owner_user_id: string | null;
  active_owners: number;
  platform_admins: number;
  available_seats: number;
  over_capacity_by: number;
  license_status: string;
  utilization_pct: number;
  license: {
    license_id: string;
    edition: string;
    licensed_seats: number;
    status: string;
    valid_until: string;
    grace_until: string;
    days_remaining: number;
  } | null;
}

/**
 * Licence and seat usage.
 *
 * The point of this screen is that the arithmetic is VISIBLE. A customer who
 * bought "5 seats" and sees six people in the user list will otherwise assume
 * they are being overcharged; showing active, minus the free owner, equals
 * billable, against licensed, is what makes the invoice explainable without a
 * support call.
 */
export function LicenceManager({ busy, act }: { busy: boolean; act: (action: () => Promise<unknown>, success: string) => void }) {
  const [capacity, setCapacity] = useState<SeatCapacity | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [token, setToken] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCapacity(await api.get<SeatCapacity>('/admin/capacity'));
      setLoadError(null);
    } catch (error) {
      setLoadError(message(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const applyLicence = (event: FormEvent) => {
    event.preventDefault();
    const value = token.trim();
    if (!value) return;
    act(async () => {
      await api.post('/admin/license', { token: value });
      // Cleared immediately: the licence token is a signed credential and
      // should not sit in a form field after it has been installed.
      setToken('');
      await load();
    }, 'Licence applied');
  };

  if (loading && !capacity) return <LoadingState label="Loading licence" />;
  if (loadError && !capacity) return <ErrorState message={loadError} onRetry={() => void load()} />;
  if (!capacity) return <EmptyState message="No licence information" />;

  const over = capacity.over_capacity_by > 0;

  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_420px]">
      <Panel title="Seat usage">
        <div className="grid gap-3 p-3">
          {over ? (
            <div className="rounded-md border border-danger/30 bg-danger-soft p-2 text-xs text-danger">
              Over capacity by {capacity.over_capacity_by}. Everyone keeps working, but no user
              can be added or reactivated until {capacity.over_capacity_by} are deactivated or
              the licence is increased.
            </div>
          ) : null}

          <table className="w-full text-base">
            <tbody>
              <tr className="border-b border-line">
                <td className="p-2 text-muted">Active people</td>
                <td className="p-2 text-right font-mono tabular-nums">{capacity.active_users}</td>
              </tr>
              <tr className="border-b border-line">
                <td className="p-2 text-muted">Owner, at no charge</td>
                <td className="p-2 text-right font-mono tabular-nums">
                  −{capacity.free_owner_seats}
                </td>
              </tr>
              {capacity.platform_admins > 0 ? (
                <tr className="border-b border-line">
                  <td className="p-2 text-muted">Konnextions support access</td>
                  <td className="p-2 text-right font-mono tabular-nums">
                    −{capacity.platform_admins}
                  </td>
                </tr>
              ) : null}
              <tr className="border-b border-line font-semibold">
                <td className="p-2">Billed seats in use</td>
                <td className="p-2 text-right font-mono tabular-nums">
                  {capacity.billable_users}
                </td>
              </tr>
              <tr className="border-b border-line">
                <td className="p-2 text-muted">Licensed</td>
                <td className="p-2 text-right font-mono tabular-nums">
                  {capacity.licensed_seats}
                </td>
              </tr>
              <tr>
                <td className="p-2 text-muted">Available</td>
                <td className={`p-2 text-right font-mono tabular-nums ${over ? 'text-danger' : ''}`}>
                  {capacity.available_seats}
                </td>
              </tr>
            </tbody>
          </table>

          {capacity.active_owners > 1 ? (
            <p className="text-xs text-faint">
              {capacity.active_owners} owners are active. Only one is free; the rest are billed
              like any other user.
            </p>
          ) : null}
        </div>
      </Panel>

      <div className="grid gap-3">
        <Panel title="Current licence">
          <div className="grid gap-2 p-3 text-base">
            {capacity.license ? (
              <>
                <div className="flex justify-between">
                  <span className="text-muted">Status</span>
                  <Badge tone={capacity.license.status === 'valid' ? 'ok' : 'danger'}>
                    {capacity.license.status}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Edition</span>
                  <span>{capacity.license.edition}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Expires</span>
                  <span className="font-mono text-xs">
                    {capacity.license.valid_until.slice(0, 10)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Days remaining</span>
                  <span className="font-mono tabular-nums">
                    {capacity.license.days_remaining}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Licence ID</span>
                  <span className="font-mono text-xs">{capacity.license.license_id}</span>
                </div>
              </>
            ) : (
              <p className="text-xs text-faint">
                No signed licence is installed. Seat limits fall back to this
                deployment&apos;s configured default.
              </p>
            )}
          </div>
        </Panel>

        <Panel title="Manage Plan" bodyClassName="p-3">
          <p className="mb-3 text-xs text-muted">
            The purchasing Owner or authorised Administrator can extend the plan,
            upgrade seats immediately, or schedule a seat reduction for the next renewal.
            Purchased duration cannot be shortened.
          </p>
          <a
            className="inline-flex h-8 items-center rounded-md bg-brand px-3 text-sm font-semibold text-bg"
            href="https://konnextions.marketingstudios.in/download/"
            target="_blank"
            rel="noreferrer"
          >
            Open protected Manage Plan
          </a>
          <p className="mt-2 text-xs text-faint">
            Sign in with the Google account used for the purchase. Local upgrades issue
            a replacement signed licence that must be applied below; Connect is updated
            on the managed service.
          </p>
        </Panel>

        <Panel title="Install a licence" bodyClassName="p-3">
          <form className="grid gap-2" onSubmit={applyLicence}>
            <label className="text-xs text-muted" htmlFor="licence-token">
              Paste the signed licence token from your .lic file
            </label>
            <textarea
              id="licence-token"
              className="min-h-32 rounded-md border border-line bg-bg p-2 font-mono text-xs"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="eyJhbGciOiJFUzI1NiIs..."
            />
            <p className="text-xs text-faint">
              Verified offline against this server&apos;s public key and bound to this
              organization. A licence issued earlier than the one already installed is
              refused, so a superseded token cannot be re-applied.
            </p>
            <Button type="submit" variant="solid" tone="brand" disabled={busy || !token.trim()}>
              Apply licence
            </Button>
          </form>
        </Panel>
      </div>
    </div>
  );
}

interface ReadinessCheck {
  ready: boolean;
  detail: string;
}

interface ConnectReadiness {
  ready_to_call: boolean;
  checks: Record<string, ReadinessCheck>;
  entitlements: Record<string, { enabled: boolean; reason: string }>;
  activation_order: string[];
}

interface ConnectorKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  created_at: string | null;
  last_used_at: string | null;
  is_active: boolean;
}

interface ConnectWebhook {
  id: string;
  name: string;
  url: string;
  event_types: string[];
  is_active: boolean;
  consecutive_failures: number;
  secret_fingerprint: string;
}

interface ConnectIdentityMapping {
  id: string;
  integration_id: string;
  external_user_id: string;
  external_user_label: string;
  user_id: string;
  user_name: string;
  user_email: string;
  is_active: boolean;
  last_synced_at: string | null;
}

export function ConnectSetup() {
  const [readiness, setReadiness] = useState<ConnectReadiness | null>(null);
  const [keys, setKeys] = useState<ConnectorKey[]>([]);
  const [createdKey, setCreatedKey] = useState<string>('');
  const [webhooks, setWebhooks] = useState<ConnectWebhook[]>([]);
  const [mappings, setMappings] = useState<ConnectIdentityMapping[]>([]);
  const [users, setUsers] = useState<OverviewUser[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [mappingIntegrationId, setMappingIntegrationId] = useState('');
  const [mappingUserId, setMappingUserId] = useState('');
  const [externalUserId, setExternalUserId] = useState('');
  const [externalUserLabel, setExternalUserLabel] = useState('Travel portal agent');
  const [createdWebhookSecret, setCreatedWebhookSecret] = useState('');
  const [keyName, setKeyName] = useState('CRM connector');
  const [webhookName, setWebhookName] = useState('CRM events');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const state = await api.get<ConnectReadiness>('/connect/readiness');
      setReadiness(state);
      try { setKeys(await api.get<ConnectorKey[]>('/connect/keys')); }
      catch { setKeys([]); }
      try {
        const [nextMappings, nextUsers, nextIntegrations] = await Promise.all([
          api.get<ConnectIdentityMapping[]>('/connect/identity-mappings'),
          api.get<OverviewUser[]>('/admin/users'),
          api.get<Integration[]>('/admin/config/integrations'),
        ]);
        setMappings(nextMappings);
        setUsers(nextUsers);
        setIntegrations(nextIntegrations.filter((item) => item.is_enabled));
        setMappingIntegrationId((current) => current || nextIntegrations.find((item) => item.is_enabled)?.id || '');
        setMappingUserId((current) => current || nextUsers[0]?.id || '');
      } catch {
        setMappings([]);
        setUsers([]);
        setIntegrations([]);
      }
      if (state.entitlements.connect_webhooks?.enabled) {
        try { setWebhooks(await api.get<ConnectWebhook[]>('/connect/webhooks')); }
        catch { setWebhooks([]); }
      } else setWebhooks([]);
    } catch (caught) { setError(message(caught)); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const createKey = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<ConnectorKey & { api_key: string }>('/connect/keys', {
        name: keyName,
        scopes: ['contacts:read', 'contacts:write', 'calls:create', 'calls:read', 'callbacks:write'],
      });
      setCreatedKey(result.api_key);
      await refresh();
    } catch (caught) { setError(message(caught)); }
    finally { setBusy(false); }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    try { await api.post(`/connect/keys/${id}/revoke`); await refresh(); }
    catch (caught) { setError(message(caught)); }
    finally { setBusy(false); }
  };

  const createWebhook = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<ConnectWebhook & { signing_secret: string }>('/connect/webhooks', {
        name: webhookName,
        url: webhookUrl,
        event_types: [],
      });
      setCreatedWebhookSecret(result.signing_secret);
      setWebhookUrl('');
      await refresh();
    } catch (caught) { setError(message(caught)); }
    finally { setBusy(false); }
  };

  const saveMapping = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/connect/identity-mappings', {
        integration_id: mappingIntegrationId,
        external_user_id: externalUserId,
        external_user_label: externalUserLabel,
        user_id: mappingUserId,
      });
      setExternalUserId('');
      await refresh();
    } catch (caught) { setError(message(caught)); }
    finally { setBusy(false); }
  };

  if (!readiness && !error) return <LoadingState label="Checking Connect readiness" />;

  return (
    <div className="space-y-3">
      {error ? <ErrorState message={error} /> : null}
      <Panel
        title="Hosted product readiness"
        actions={<Button size="sm" onClick={() => void refresh()}>Recheck</Button>}
        bodyClassName="p-3"
      >
        <div className="mb-3 flex items-center gap-2">
          <Badge tone={readiness?.ready_to_call ? 'ok' : 'warn'}>
            {readiness?.ready_to_call ? 'Ready to place calls' : 'Activation incomplete'}
          </Badge>
          <span className="text-xs text-muted">Provider calling remains disabled until its live credential test passes.</span>
        </div>
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {Object.entries(readiness?.checks ?? {}).map(([name, check]) => (
            <div key={name} className="rounded-md border border-line bg-bg p-3">
              <div className="flex items-center gap-2">
                <Badge tone={check.ready ? 'ok' : 'neutral'}>{check.ready ? 'Ready' : 'Pending'}</Badge>
                <strong className="text-sm">{name.replace(/_/g, ' ')}</strong>
              </div>
              <p className="mt-2 text-xs text-muted">{check.detail}</p>
            </div>
          ))}
        </div>
        <ol className="mt-4 list-decimal space-y-1 pl-5 text-xs text-muted">
          {(readiness?.activation_order ?? []).map((step) => <li key={step}>{step}</li>)}
        </ol>
      </Panel>

      <Panel title="Product entitlements" bodyClassName="p-3">
        <div className="flex flex-wrap gap-2">
          {Object.entries(readiness?.entitlements ?? {}).map(([name, state]) => (
            <Badge key={name} tone={state.enabled ? 'ok' : 'neutral'}>
              {name.replace(/_/g, ' ')} · {state.enabled ? 'enabled' : 'not licensed'}
            </Badge>
          ))}
        </div>
      </Panel>

      <Panel title="External agent mapping" bodyClassName="space-y-3 p-3">
        <p className="text-xs text-muted">Map each external CRM or Travel portal agent ID to a real Konnextions user. Agents never receive connector keys or provider credentials.</p>
        <div className="grid max-w-5xl gap-2 md:grid-cols-4">
          <select className={inputClass} value={mappingIntegrationId} onChange={(event) => setMappingIntegrationId(event.target.value)}>
            <option value="">Choose integration</option>
            {integrations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <input className={inputClass} value={externalUserId} onChange={(event) => setExternalUserId(event.target.value)} placeholder="External agent ID" />
          <select className={inputClass} value={mappingUserId} onChange={(event) => setMappingUserId(event.target.value)}>
            <option value="">Choose Konnextions user</option>
            {users.map((item) => <option key={item.id} value={item.id}>{item.full_name}</option>)}
          </select>
          <Button variant="solid" tone="brand" disabled={busy || !mappingIntegrationId || !mappingUserId || !externalUserId.trim()} onClick={() => void saveMapping()}>Save mapping</Button>
        </div>
        <input className={`${inputClass} max-w-xl w-full`} value={externalUserLabel} onChange={(event) => setExternalUserLabel(event.target.value)} placeholder="Friendly label" />
        {mappings.length ? (
          <div className="overflow-auto"><table className="w-full text-sm"><thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-2">External agent</th><th>Konnextions user</th><th>State</th><th /></tr></thead><tbody>{mappings.map((item) => <tr key={item.id} className="border-b border-line/60"><td className="p-2"><div>{item.external_user_label || item.external_user_id}</div><code className="text-xs">{item.external_user_id}</code></td><td><div>{item.user_name}</div><div className="text-xs text-muted">{item.user_email}</div></td><td><Badge tone={item.is_active ? 'ok' : 'neutral'}>{item.is_active ? 'Active' : 'Disabled'}</Badge></td><td>{item.is_active ? <Button size="sm" tone="danger" disabled={busy} onClick={async () => { setBusy(true); try { await api.post(`/connect/identity-mappings/${item.id}/disable`); await refresh(); } catch (caught) { setError(message(caught)); } finally { setBusy(false); } }}>Disable</Button> : null}</td></tr>)}</tbody></table></div>
        ) : <EmptyState message="No external agents mapped" hint="Create an integration first, then map its agent IDs here." />}
      </Panel>

      <Panel title="Connector API keys" bodyClassName="space-y-3 p-3">
        <p className="text-xs text-muted">Only create a key when another product, such as a CRM, must create contacts or calls. Provider passwords never belong here.</p>
        <div className="flex max-w-xl gap-2">
          <input className={`${inputClass} flex-1`} value={keyName} onChange={(event) => setKeyName(event.target.value)} />
          <Button variant="solid" tone="brand" disabled={busy || !keyName.trim()} onClick={() => void createKey()}>Create key</Button>
        </div>
        {createdKey ? (
          <div className="rounded-md border border-warn/40 bg-warn-soft p-3">
            <strong className="text-sm">Copy this key now—it will not be shown again.</strong>
            <div className="mt-2 flex gap-2"><code className="min-w-0 flex-1 overflow-auto rounded bg-surface p-2 text-xs">{createdKey}</code><Button size="sm" onClick={() => void navigator.clipboard.writeText(createdKey)}>Copy</Button></div>
          </div>
        ) : null}
        {keys.length ? (
          <div className="overflow-auto"><table className="w-full text-sm"><thead><tr className="border-b border-line text-left text-xs text-faint"><th className="p-2">Name</th><th>Prefix</th><th>Scopes</th><th>Last used</th><th /></tr></thead><tbody>{keys.map((key) => <tr key={key.id} className="border-b border-line/60"><td className="p-2">{key.name}</td><td><code>{key.key_prefix}</code></td><td className="text-xs">{key.scopes.join(', ')}</td><td className="text-xs">{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}</td><td><Button size="sm" tone="danger" disabled={busy || !key.is_active} onClick={() => void revoke(key.id)}>{key.is_active ? 'Revoke' : 'Revoked'}</Button></td></tr>)}</tbody></table></div>
        ) : <EmptyState message="No connector keys" hint="That is correct until an external system needs API access." />}
      </Panel>

      <Panel title="Signed event webhooks" bodyClassName="space-y-3 p-3">
        <p className="text-xs text-muted">Send call and agent events to a customer's CRM. Deliveries are signed, retried in the background and isolated to this organization.</p>
        <div className="grid max-w-3xl gap-2 md:grid-cols-[1fr_2fr_auto]">
          <input className={inputClass} value={webhookName} onChange={(event) => setWebhookName(event.target.value)} placeholder="Connection name" />
          <input className={inputClass} value={webhookUrl} onChange={(event) => setWebhookUrl(event.target.value)} placeholder="https://crm.example.com/webhooks/konnextions" />
          <Button variant="solid" tone="brand" disabled={busy || !webhookName.trim() || !webhookUrl.trim()} onClick={() => void createWebhook()}>Add webhook</Button>
        </div>
        {createdWebhookSecret ? (
          <div className="rounded-md border border-warn/40 bg-warn-soft p-3">
            <strong className="text-sm">Copy the signing secret now—it will not be shown again.</strong>
            <div className="mt-2 flex gap-2"><code className="min-w-0 flex-1 overflow-auto rounded bg-surface p-2 text-xs">{createdWebhookSecret}</code><Button size="sm" onClick={() => void navigator.clipboard.writeText(createdWebhookSecret)}>Copy</Button></div>
          </div>
        ) : null}
        {webhooks.length ? <div className="grid gap-2">{webhooks.map((hook) => <div key={hook.id} className="flex items-center gap-3 rounded-md border border-line p-3"><div className="min-w-0 flex-1"><div className="font-medium">{hook.name}</div><div className="truncate text-xs text-muted">{hook.url}</div><div className="text-xs text-faint">Secret fingerprint: {hook.secret_fingerprint} · failures: {hook.consecutive_failures}</div></div><Badge tone={hook.is_active ? 'ok' : 'neutral'}>{hook.is_active ? 'Active' : 'Disabled'}</Badge>{hook.is_active ? <Button size="sm" tone="danger" disabled={busy} onClick={async () => { setBusy(true); try { await api.post(`/connect/webhooks/${hook.id}/disable`); await refresh(); } catch (caught) { setError(message(caught)); } finally { setBusy(false); } }}>Disable</Button> : null}</div>)}</div> : <EmptyState message="No webhooks configured" hint="Optional unless this customer connects an external CRM." />}
      </Panel>
    </div>
  );
}

export function ProvidersManager({ busy, act }: { busy: boolean; act: (action: () => Promise<unknown>, success: string) => void }) {
  const [catalogue, setCatalogue] = useState<ProviderSummary[]>([]);
  const [connection, setConnection] = useState<ConnectionStatus | null>(null);
  const [numbers, setNumbers] = useState<ConnectedNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [schema, setSchema] = useState<ProviderSchema | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const [manualForm, setManualForm] = useState({ e164: '', friendly_name: '', country: 'US' });
  const [manualRequired, setManualRequired] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextCatalogue, nextConnection] = await Promise.all([
        api.get<{ providers: ProviderSummary[] }>('/providers/catalogue'),
        api.get<ConnectionStatus>('/providers/connection'),
      ]);
      setCatalogue(nextCatalogue.providers);
      setConnection(nextConnection);
      if (nextConnection.provider_id) {
        const rows = await api.get<ConnectedNumber[]>('/admin/phone-numbers').catch(() => []);
        setNumbers(rows.filter((row) => row.provider === nextConnection.provider_id));
      } else {
        setNumbers([]);
      }
      setLoadError(null);
    } catch (caught) {
      setLoadError(message(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const loadSchema = useCallback(async (providerId: string) => {
    setSchemaError(null);
    setManualRequired(false);
    try {
      const next = await api.get<ProviderSchema>(`/providers/catalogue/${providerId}/schema`);
      setSchema(next);
      // Non-secret fields the connection already has (if this IS the
      // currently connected provider) are pre-filled so a credential
      // rotation only requires re-entering the one field being replaced.
      // Secret fields are NEVER pre-filled — the server never returns their
      // value, by design, so there is nothing to pre-fill even if we wanted to.
      const currentConfig = connection?.provider_id === providerId ? connection.config ?? {} : {};
      const initial: Record<string, string> = {};
      for (const field of next.fields) {
        const existing = currentConfig[field.id];
        if (!field.secret && existing != null) initial[field.id] = String(existing);
      }
      setFieldValues(initial);
    } catch (caught) {
      setSchema(null);
      setSchemaError(message(caught));
    }
  }, [connection]);

  const selectProvider = (providerId: string) => {
    setSelectedId(providerId);
    void loadSchema(providerId);
  };

  const visibleCatalogue = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return catalogue;
    return catalogue.filter((item) =>
      item.display_name.toLowerCase().includes(query) ||
      item.provider_id.toLowerCase().includes(query) ||
      item.regions.some((region) => region.toLowerCase().includes(query)),
    );
  }, [catalogue, search]);

  const visibleFields = useMemo(() => {
    if (!schema) return [];
    return schema.fields.filter((field) => {
      if (!field.depends_on) return true;
      return fieldValues[field.depends_on.field] === field.depends_on.value;
    });
  }, [schema, fieldValues]);

  const connect = (event: FormEvent) => {
    event.preventDefault();
    if (!schema) return;
    const missing = visibleFields.find((field) => field.required && !fieldValues[field.id]?.trim());
    if (missing) {
      setSchemaError(`'${missing.label}' is required`);
      return;
    }
    const fields: Record<string, string> = {};
    for (const field of visibleFields) {
      const value = fieldValues[field.id]?.trim();
      if (value) fields[field.id] = value;
    }
    void act(async () => {
      const result = await api.put<ConnectionStatus>('/providers/connection', {
        provider_id: schema.provider_id,
        fields,
      });
      setConnection(result);
      setSyncNotice(null);
      setManualRequired(false);
      // Secret values live only in this form's local state, and only until
      // now — clear them immediately after a successful submit so a
      // credential never lingers in memory (or a browser's form-restore
      // cache) longer than the single request that needed it.
      setFieldValues((current) => {
        const cleared = { ...current };
        for (const field of schema.fields) if (field.secret) delete cleared[field.id];
        return cleared;
      });
      await load();
    }, `Connected to ${schema.display_name}`);
  };

  const testConnection = () => {
    void act(async () => {
      const result = await api.post<ConnectionStatus>('/providers/connection/test');
      // last_test_message/last_test_success render from `connection` alone
      // (in the "Current connection" panel) — no separate testResult state,
      // so the outcome is shown exactly once, not duplicated in two panels.
      setConnection(result);
    }, 'Connection test complete');
  };

  const syncNumbers = () => {
    setManualRequired(false);
    void act(async () => {
      try {
        const result = await api.post<{ created: number; updated: number; deactivated: number }>(
          '/providers/connection/numbers/sync',
        );
        setSyncNotice(
          `${result.created} added, ${result.updated} updated, ${result.deactivated} deactivated`,
        );
        await load();
      } catch (caught) {
        if (caught instanceof ApiError && caught.details.manual_import_required) {
          setManualRequired(true);
          setSyncNotice(null);
          return;
        }
        throw caught;
      }
    }, 'Number sync complete');
  };

  const addManualNumber = (event: FormEvent) => {
    event.preventDefault();
    void act(async () => {
      await api.post('/providers/connection/numbers/manual', manualForm);
      setManualForm({ e164: '', friendly_name: '', country: 'US' });
      await load();
    }, 'Number added');
  };

  const disableConnection = () => {
    const reason = window.prompt('Reason for disabling this connection (visible in audit history):', '');
    if (reason === null) return;
    void act(async () => {
      const result = await api.post<ConnectionStatus>('/providers/connection/disable', { reason });
      setConnection(result);
      setSchema(null);
      setSelectedId(null);
    }, 'Provider connection disabled');
  };

  if (loading && !connection) return <LoadingState label="Loading provider catalogue" />;

  return (
    <div className="space-y-3">
      {loadError ? <div className="border border-danger/30 bg-danger-soft p-3 text-xs text-danger">{loadError}</div> : null}

      {connection?.provider_id ? (
        <Panel
          title="Current connection"
          actions={<Button size="sm" tone="danger" disabled={busy} onClick={disableConnection}>Disable</Button>}
          bodyClassName="p-3"
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Provider</div>
              <div className="mt-1 font-medium">
                {catalogue.find((p) => p.provider_id === connection.provider_id)?.display_name ?? connection.provider_id}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Status</div>
              <Badge tone={connection.status === 'verified' ? 'ok' : connection.status === 'disabled' ? 'neutral' : 'warn'}>
                {connection.status}
              </Badge>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Last test</div>
              {connection.last_tested_at ? (
                <Badge tone={connection.last_test_success ? 'ok' : 'danger'}>
                  {connection.last_test_success ? 'Passed' : (connection.last_test_code || 'Failed')}
                </Badge>
              ) : <span className="text-sm text-faint">Never tested</span>}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-faint">Numbers synced</div>
              <div className="mt-1 text-sm">
                {connection.last_numbers_synced_at
                  ? new Date(connection.last_numbers_synced_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
                  : 'Never'}
              </div>
            </div>
          </div>

          {connection.last_test_message ? (
            <p className={`mt-3 text-xs ${connection.last_test_success ? 'text-ok' : 'text-danger'}`}>
              {connection.last_test_message}
            </p>
          ) : null}

          {connection.capabilities_granted?.length ? (
            <div className="mt-3 flex flex-wrap gap-1">
              {connection.capabilities_granted.map((cap) => <Badge key={cap} tone="info">{capabilityLabel(cap)}</Badge>)}
            </div>
          ) : null}

          {connection.secret_fields_configured?.length ? (
            <p className="mt-3 text-xs text-faint">
              Configured secret fields: {connection.secret_fields_configured.join(', ')} (values are never shown again)
            </p>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
            <Button size="sm" variant="solid" tone="brand" disabled={busy} onClick={testConnection}>Test connection</Button>
            <Button size="sm" disabled={busy} onClick={syncNumbers}>Sync numbers</Button>
            {syncNotice ? <span className="text-xs text-muted">{syncNotice}</span> : null}
          </div>

          {manualRequired ? (
            <form className="mt-3 flex flex-wrap items-end gap-2 border-t border-line pt-3" onSubmit={addManualNumber}>
              <p className="w-full text-xs text-warn">
                This provider does not expose a number inventory API — add the number manually.
              </p>
              <input required className={inputClass} placeholder="+14155551234" value={manualForm.e164} onChange={(e) => setManualForm({ ...manualForm, e164: e.target.value })} />
              <input className={inputClass} placeholder="Friendly name" value={manualForm.friendly_name} onChange={(e) => setManualForm({ ...manualForm, friendly_name: e.target.value })} />
              <input className={`${inputClass} w-16`} maxLength={2} placeholder="US" value={manualForm.country} onChange={(e) => setManualForm({ ...manualForm, country: e.target.value.toUpperCase() })} />
              <Button size="sm" type="submit" variant="solid" tone="brand" disabled={busy}>Add number</Button>
            </form>
          ) : null}

          {numbers.length ? (
            <div className="mt-3 border-t border-line pt-3">
              <div className="mb-1 text-xs uppercase tracking-wide text-faint">Numbers on this connection</div>
              <div className="flex flex-wrap gap-1">
                {numbers.map((num) => <Badge key={num.id} tone="neutral" className="knx-num">{num.e164}</Badge>)}
              </div>
            </div>
          ) : null}
        </Panel>
      ) : (
        <Panel bodyClassName="p-3">
          <EmptyState message="No calling provider connected" hint="Choose a provider below to get started." />
        </Panel>
      )}

      <div className="grid gap-3 lg:grid-cols-[1fr_420px]">
        <Panel
          title="Provider catalogue"
          actions={
            <input
              className={inputClass}
              placeholder="Search providers or regions"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search provider catalogue"
            />
          }
        >
          {visibleCatalogue.length ? (
            <div className="grid gap-2 p-3 sm:grid-cols-2">
              {visibleCatalogue.map((item) => (
                <button
                  key={item.provider_id}
                  type="button"
                  onClick={() => selectProvider(item.provider_id)}
                  className={`rounded-md border p-3 text-left transition-colors ${
                    selectedId === item.provider_id
                      ? 'border-brand bg-brand-soft'
                      : 'border-line bg-surface hover:border-brand/50 hover:bg-surface-2'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{item.display_name}</span>
                    {connection?.provider_id === item.provider_id ? <Badge tone="ok">Connected</Badge> : null}
                  </div>
                  <div className="mt-1 text-xs text-faint">
                    {item.regions.join(', ')} · {item.connection_mode.replace(/_/g, ' ')}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {item.capabilities.slice(0, 4).map((cap) => (
                      <span key={cap} className="rounded-sm bg-surface-2 px-1.5 py-0.5 text-2xs text-muted">
                        {capabilityLabel(cap)}
                      </span>
                    ))}
                    {item.capabilities.length > 4 ? (
                      <span className="rounded-sm bg-surface-2 px-1.5 py-0.5 text-2xs text-muted">
                        +{item.capabilities.length - 4} more
                      </span>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <EmptyState
              message={catalogue.length ? 'No providers match that search' : 'No providers are available yet'}
              hint={catalogue.length ? undefined : 'Providers appear here only once they pass the full readiness gate.'}
            />
          )}
        </Panel>

        <Panel title={schema ? `Connect ${schema.display_name}` : 'Provider credentials'} bodyClassName="p-3">
          {!selectedId ? (
            <EmptyState message="Select a provider" hint="Choose one from the catalogue to see its credential form." />
          ) : schemaError && !schema ? (
            <div className="text-sm text-danger">{schemaError}</div>
          ) : schema ? (
            <form className="grid gap-3" onSubmit={connect}>
              <a href={schema.docs_url} target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline">
                {schema.display_name} setup documentation ↗
              </a>
              {visibleFields.map((field) => (
                <label key={field.id} className="grid gap-1">
                  <span className="text-xs font-medium text-muted">
                    {field.label}
                    {field.required ? <span className="text-danger"> *</span> : null}
                  </span>
                  {field.type === 'boolean' ? (
                    <input
                      type="checkbox"
                      checked={fieldValues[field.id] === 'true'}
                      onChange={(e) => setFieldValues({ ...fieldValues, [field.id]: e.target.checked ? 'true' : '' })}
                    />
                  ) : field.type === 'select' && field.options ? (
                    <select
                      className={inputClass}
                      value={fieldValues[field.id] ?? ''}
                      onChange={(e) => setFieldValues({ ...fieldValues, [field.id]: e.target.value })}
                    >
                      <option value="">Select…</option>
                      {field.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  ) : field.type === 'oauth' || field.type === 'private_key' || field.type === 'certificate' ? (
                    <div className="rounded-md border border-line bg-surface-2 p-2 text-xs text-faint">
                      This field type ({field.type}) is not yet supported by this form. Contact support to
                      configure it server-side.
                    </div>
                  ) : (
                    <input
                      className={inputClass}
                      type={field.type === 'secret' ? 'password' : field.type === 'url' ? 'url' : 'text'}
                      placeholder={
                        field.secret && connection?.secret_fields_configured?.includes(field.id)
                          ? 'Currently set — leave blank to keep'
                          : field.placeholder
                      }
                      value={fieldValues[field.id] ?? ''}
                      onChange={(e) => setFieldValues({ ...fieldValues, [field.id]: e.target.value })}
                      autoComplete={field.secret ? 'new-password' : 'off'}
                    />
                  )}
                  {field.help_text ? <span className="text-2xs text-faint">{field.help_text}</span> : null}
                </label>
              ))}
              {schemaError ? <div className="text-xs text-danger">{schemaError}</div> : null}
              <Button type="submit" variant="solid" tone="brand" disabled={busy}>
                {connection?.provider_id === schema.provider_id ? 'Update connection' : 'Connect'}
              </Button>
              <p className="text-2xs text-faint">
                Secret fields are encrypted on the server and are never sent back to this browser after saving.
              </p>
            </form>
          ) : (
            <LoadingState label="Loading credential form" />
          )}
        </Panel>
      </div>
    </div>
  );
}
